import React, { useState, useEffect, useCallback, useRef } from 'react';
import TimePicker from '../components/TimePicker';
import GoalGuardrailModal from '../components/GoalGuardrailModal';
import LoadBalanceModal from '../components/LoadBalanceModal';
import { validateUserGoal, validateKmDaysMatrix, weeklyKmGuidance } from '../utils/goalValidator';
import { runningMissingFields, runningMissingMessage, runningMatrixCheck, runningDaysCount, runningPlanCheck,
         runningPlanChanged, fetchRunningAvailability, allowedRangeText, saveErrorMessage } from '../utils/runningSaveRules';
import authFetch from '../utils/authFetch';
import { planRegenRequest } from '../utils/planRegenRequest';
import { showToast } from '../utils/toast';
import { CYCLING_ENABLED, GOOGLE_SIGNIN_ENABLED } from '../config/features';
import DeleteAccountSection from '../components/DeleteAccountSection';
import PasswordSetupSection from '../components/profile/PasswordSetupSection';
import NotificationSettings from '../components/NotificationSettings';
import TrainingConnectionsSection from '../components/TrainingConnectionsSection';
import { setNavigationGuard, confirmNavigation } from '../utils/navigationGuard';
import {
  CARD, INPUT, LABEL, HELPER, PROFILE_COLORS, SectionLabel, CardTitle, DayChips, Segmented, Collapsible, LevelMeter,
  hrZones, ZoneBar, StickySaveBar,
} from '../components/profile/ProfileParts';
import useSaveDialog from '../components/profile/useSaveDialog';
import { planRebuildChange, runPlanRebuild, getRebuildStatus, REBUILD_WARNING, REBUILD_CONFIRM_LABEL } from '../utils/planRebuild';
import { isoToParts, evaluateDobParts } from '../utils/age';
import { Gear, House, User, HandWaving, CheckCircle, Check } from '@phosphor-icons/react';
import DateOfBirthInput from '../components/profile/DateOfBirthInput';
import { useBackEntry } from '../hooks/useBackEntry';

// Map validator labels → internal goal_distance ('5'/'10'/'21.1'/'42.2').
const profileLabelToDistance = (lbl) => ({
  '5K': '5', '10K': '10', 'חצי מרתון': '21.1', 'מרתון': '42.2',
}[lbl] || null);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper function to translate level from English to Hebrew
// Running-context level label (Phase 3 terminology): מתחיל / מנוסה / מתקדם.
// Legacy running value 'intermediate' reads as מנוסה (Experienced) — never
// 'בינוני' for the active running product (cycling keeps its own labels).
const getLevelLabel = (level) => {
  const levelMap = {
    'beginner': 'מתחיל',
    'experienced': 'מנוסה',
    'intermediate': 'מנוסה',   // legacy running → Experienced (read-only mapping)
    'advanced': 'מתקדם',
    'competitive': 'תחרותי',
    'professional': 'מקצועי'
  };
  return levelMap[level] || level;
};

// Profile V2 (2026-10-19): unsaved-changes tracking for the running-profile form (the fields the
// "שמירה" bar saves through saveAllProfiles). Frontend only — compares the form to the last loaded /
// saved values; normalised to strings so 40 and "40" or null and "" are the same value.
const LEAVE_PROFILE_MSG = 'יש שינויים בפרופיל הריצה שלא נשמרו. לצאת בלי לשמור?';
const EMPTY_CYCLING = {
  weekly_cycling_hours: '', cycling_goal_event: '', cycling_level: '', cycling_goal_date: '',
  cycling_goal_distance: '', has_cycling_race: null, preferred_cycling_days: '', max_hr: '',
  recent_longest_ride_km: '',
};
function formSignature(running, cycling, sports) {
  const norm = (o) => Object.keys(o || {}).sort().map((k) => [k, o[k] == null ? '' : String(o[k])]);
  return JSON.stringify([norm(running), norm(cycling), [...(sports || [])].map(String).sort()]);
}
// The healthy state reads "כשיר" (owner 2026-11-01, UI only — the stored value stays 'ok'). No status dots: the
// selected option is marked by the shared accent alone (Segmented).
export const INJURY_OPTIONS = [
  { value: 'ok', label: 'כשיר', desc: 'אין פציעה פעילה' },
  { value: 'pain', label: 'כאב', desc: 'מתאמן עם כאב או פציעה קלה' },
  { value: 'cant_run', label: 'לא רץ', desc: 'מנוחה מלאה מהספורט' },
];

// pain -> ok (owner 2026-11-01): the athlete confirms the gradual return BEFORE the save — the backend then builds the
// one transition week (<= 80% volume, at most one quality session) and the full plan from the week after.
export const PAIN_EXIT_DIALOG = {
  kind: 'confirm',
  title: 'חזרה הדרגתית לאימונים',
  message: "עדכנת את מצבך מ'כאב' ל'כשיר'.\n\n"
    + 'בשבוע המעבר התוכנית תכלול עד 80% מהנפח הרגיל ועד אימון איכות אחד. מהשבוע השני תחזור לתוכנית האימונים המלאה.\n\n'
    + "אם הכאב נמשך או חוזר במהלך האימונים, יש לעדכן שוב את מצבך ל'כאב' כדי שהתוכנית תותאם בהתאם.",
  confirmLabel: 'אישור וחזרה הדרגתית',
};

// 🚴🏃 Sports Profile Section Component - Running & Cycling
const SportsProfileSection = ({ userId, onUpdate, onBack, onDirtyChange, focusSection }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // LoadBalanceModal — opens when the user clicks save with an
  // invalid km × days combo. See onFix / onSaveAnyway handlers near
  // the JSX at the bottom of the file.
  const [loadBalanceModal, setLoadBalanceModal] = useState({ open: false });
  const [sportsActive, setSportsActive] = useState(['running']);
  const [goalTimePickerOpen, setGoalTimePickerOpen] = useState(false);
  const [guardrail, setGuardrail] = useState(null); // {result} | null

  // ── Profile-change regenerate flow (2026-05-31) ────────────────
  // After the user clicks "save profile", if any plan-shaping field
  // changed (km / level / goal_distance / goal_time / preferred_days)
  // we pop a toast asking whether to regenerate the active plans to
  // reflect the new profile. Gated to once-per-24h server-side via
  // GET /profile/can-regenerate.
  //
  // mode:
  //   'ask'        → "want me to regenerate?" with [Yes] [Not now]
  //   'blocked'    → "already used today, try again in X hours"
  //   'generating' → spinner while the server is rebuilding the plan
  //   'done'       → success confirmation, auto-dismisses
  const [regenerateToast, setRegenerateToast] = useState(null);
  // Snapshot of the profile fields at last successful load/save.
  // Used to detect drift on the next save. Stored as a ref so it
  // doesn't trigger re-renders on every state change.
  const lastSavedSnapshotRef = useRef(null);
  const _savingInFlightRef = useRef(false);

  // Sticky session-touched set. We add a field name when its value
  // FIRST diverges from the last-saved baseline, and we DON'T remove
  // it if the user reverts. Reason: user reports a real bug where
  // they tried km=29, got hard-blocked, fixed it back to km=35 (the
  // original) and saved — no toast appeared because the net delta
  // was zero. Semantically they did edit the field, so we should
  // still ask about regenerate. Cleared on each successful save.
  const touchedFieldsRef = useRef(new Set());

  // In-app save dialog (replaces the browser's alert / confirm in the save flow; same texts, same rules).
  // showBlock: the save stops (returns at once — the overlay keeps the form untouchable until it closes).
  // open: a confirmation (the pain-exit and rebuild questions); resolves true / false.
  // The same hook drives Onboarding's save dialog (one mechanism for both screens).
  const { open: openSaveDialog, showBlock, element: saveDialogElement } = useSaveDialog();

  // Last loaded / saved form values (Profile V2): the "ביטול" target and the unsaved-changes baseline.
  const savedFormRef = useRef(null);
  const [savedSig, setSavedSig] = useState(null);
  const markSaved = useCallback((running, cycling, sports) => {
    savedFormRef.current = { running, cycling, sports };
    setSavedSig(formSignature(running, cycling, sports));
  }, []);
  
  // Cycling data
  // Phase 1: road-only cycling — no bike picker, no FTP/power, no weight.
  const [cyclingData, setCyclingData] = useState(EMPTY_CYCLING);

  // Running data
  const [runningData, setRunningData] = useState({
    level: '',
    goal_race: '',
    goal_distance: '',
    goal_date: '',
    goal_time: '',
    avg_weekly_km: '',
    max_hr: '',
    preferred_days: '',
    preferred_long_run_day: '',
    strength_training: false,
    strength_days_per_week: '',
    injury_prone_areas: '',
    // Injury model v2 (2026-06-25): 3-state + discipline + description
    injury_state: 'ok',
    injury_discipline: '',
    injury_description: '',
    injury_start_date: null,
    chronic_sensitivities: '',
  });

  // The plan engine's answer for the running fields on screen (GET /policy/running-availability), shown under the
  // weekly-km field before saving (owner 2026-11-02). Debounced; a stale answer is dropped; null = nothing to show.
  // Display only — the save asks again (runningPlanCheck) and PUT /profile enforces it.
  const [planHint, setPlanHint] = useState(null);
  const planHintSeq = useRef(0);
  useEffect(() => {
    const d = { goal_distance: runningData.goal_distance, avg_weekly_km: runningData.avg_weekly_km,
                preferred_days: runningData.preferred_days, level: runningData.level,
                preferred_long_run_day: runningData.preferred_long_run_day };
    const seq = ++planHintSeq.current;
    // the allowed range needs only the goal and the day count; the verdict also needs the weekly km
    if (!d.goal_distance || !runningDaysCount(d.preferred_days)) { setPlanHint(null); return undefined; }
    const t = setTimeout(async () => {
      const av = await fetchRunningAvailability(authFetch, API_BASE_URL, d);
      if (seq === planHintSeq.current) setPlanHint(av);
    }, 400);
    return () => clearTimeout(t);
  }, [runningData.goal_distance, runningData.avg_weekly_km, runningData.preferred_days, runningData.level,
      runningData.preferred_long_run_day]);

  // Days of the week
  const daysOfWeek = [
    { key: 'ראשון', label: 'א' },
    { key: 'שני', label: 'ב' },
    { key: 'שלישי', label: 'ג' },
    { key: 'רביעי', label: 'ד' },
    { key: 'חמישי', label: 'ה' },
    { key: 'שישי', label: 'ו' },
    { key: 'שבת', label: 'ש' }
  ];

  const loadProfiles = useCallback(async () => {
    try {
      setLoading(true);

      // Load cycling profile
      const cyclingRes = await authFetch(`${API_BASE_URL}/profile/cycling/${userId}`);
      const cyclingProfile = await cyclingRes.json();
      let loadedSports = ['running'];
      let loadedCycling = EMPTY_CYCLING;
      if (cyclingProfile && !cyclingProfile.error) {
        loadedSports = cyclingProfile.sports_active ? cyclingProfile.sports_active.split(',') : ['running'];
        setSportsActive(loadedSports);
        loadedCycling = {
          weekly_cycling_hours: cyclingProfile.weekly_cycling_hours ? String(cyclingProfile.weekly_cycling_hours) : '',
          cycling_goal_event: cyclingProfile.cycling_goal_event || '',
          cycling_level: cyclingProfile.cycling_level || '',
          cycling_goal_date: cyclingProfile.cycling_goal_date || '',
          cycling_goal_distance: cyclingProfile.cycling_goal_distance ? String(cyclingProfile.cycling_goal_distance) : '',
          has_cycling_race: cyclingProfile.has_race != null ? cyclingProfile.has_race : null,
          preferred_cycling_days: cyclingProfile.preferred_cycling_days || '',
          max_hr: cyclingProfile.max_hr ? String(cyclingProfile.max_hr) : '',
          recent_longest_ride_km: cyclingProfile.recent_longest_ride_km ? String(cyclingProfile.recent_longest_ride_km) : '',
        };
        setCyclingData(loadedCycling);
      }

      // Load running profile (from main profile). authFetch attaches the
      // Firebase ID token so the backend require_user_token dependency
      // can verify the requester owns this user_id.
      const profileRes = await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`);
      const profile = await profileRes.json();
      if (profile && !profile.error) {
        const loadedRunning = {
          level: profile.level || '',
          goal_race: profile.goal_race || '',
          goal_distance: profile.goal_distance || '',
          goal_date: profile.goal_date || '',
          goal_time: profile.goal_time || '',
          avg_weekly_km: profile.avg_weekly_km || '',
          max_hr: profile.max_hr || '',
          preferred_days: profile.preferred_days || '',
          preferred_long_run_day: profile.preferred_long_run_day || '',
          strength_training: profile.strength_training || false,
          strength_days_per_week: profile.strength_days_per_week || '',
          injury_prone_areas: profile.injury_prone_areas || '',
          injury_state: profile.injury_state || 'ok',
          injury_discipline: profile.injury_discipline || '',
          injury_start_date: profile.injury_start_date || null,
          injury_description: profile.injury_description || '',
          chronic_sensitivities: profile.chronic_sensitivities || '',
        };
        setRunningData(loadedRunning);
        markSaved(loadedRunning, loadedCycling, loadedSports);
        // Baseline snapshot: only the fields that drive plan structure.
        // A change in any of these on the next save → regenerate-toast.
        // Cycling fields are seeded from cyclingProfile loaded above —
        // user feedback 2026-06-04: changing cycling fields produced
        // no regenerate-toast (so no progress-bar banner) because
        // touched detection only watched running fields.
        const _hadCyclingProfile = cyclingProfile && !cyclingProfile.error;
        lastSavedSnapshotRef.current = {
          avg_weekly_km:  profile.avg_weekly_km || '',
          goal_distance:  profile.goal_distance || '',
          level:          profile.level || '',
          preferred_days: profile.preferred_days || '',
          goal_time:      profile.goal_time || '',
          // Injury lifecycle fields — touched detection treats a change
          // in any of these as plan-shaping (severe→all-rest, mild/medium
          // changes intensity ceiling, rehab_week changes volume caps).
          injury_state:           profile.injury_state || 'ok',
          injury_discipline:      profile.injury_discipline || '',
          injury_start_date:      profile.injury_start_date || null,
          chronic_sensitivities:  profile.chronic_sensitivities || '',
          // Cycling fields. Sorted comma list so toggling
          // "running" ↔ "running,cycling" produces a stable diff.
          sports_active: _hadCyclingProfile
            ? ((cyclingProfile.sports_active || 'running').split(',').map(s => s.trim()).filter(Boolean).sort().join(','))
            : 'running',
          weekly_cycling_hours: _hadCyclingProfile
            ? (cyclingProfile.weekly_cycling_hours ? String(cyclingProfile.weekly_cycling_hours) : '')
            : '',
          preferred_cycling_days: _hadCyclingProfile ? (cyclingProfile.preferred_cycling_days || '') : '',
          cycling_level:          _hadCyclingProfile ? (cyclingProfile.cycling_level || '')          : '',
          cycling_goal_event:     _hadCyclingProfile ? (cyclingProfile.cycling_goal_event || '')     : '',
          cycling_goal_date:      _hadCyclingProfile ? (cyclingProfile.cycling_goal_date || '')      : '',
          cycling_goal_distance:  _hadCyclingProfile
            ? (cyclingProfile.cycling_goal_distance ? String(cyclingProfile.cycling_goal_distance) : '')
            : '',
          recent_longest_ride_km: _hadCyclingProfile
            ? (cyclingProfile.recent_longest_ride_km ? String(cyclingProfile.recent_longest_ride_km) : '')
            : '',
        };
      }
    } catch (err) { console.error('Error loading profiles:', err); }
    finally { setLoading(false); }
  }, [userId, markSaved]);

  useEffect(() => { if (userId) loadProfiles(); }, [userId, loadProfiles]);

  // ── Unsaved changes (Profile V2) ──────────────────────────────────
  // dirty = the running-profile form differs from what was last loaded / saved. Drives the sticky
  // save bar, the leave-screen confirmation and the browser's unload prompt. Notification toggles,
  // Garmin actions and personal details are separate state and never touch it.
  const dirty = savedSig !== null && formSignature(runningData, cyclingData, sportsActive) !== savedSig;
  useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  // 2026-10-29: opened from the chat's "מעבר לפרופיל" -> straight to that field's card (once, after loading)
  const focusedRef = useRef(false);
  useEffect(() => {
    if (loading || !focusSection || focusedRef.current) return;
    focusedRef.current = true;
    const el = typeof document !== 'undefined' && document.querySelector(`[data-testid="pf-${focusSection}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, focusSection]);
  useEffect(() => {
    if (!dirty) return undefined;
    const unguard = setNavigationGuard(() => window.confirm(LEAVE_PROFILE_MSG));
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => { unguard(); window.removeEventListener('beforeunload', onUnload); };
  }, [dirty]);
  // Android / browser Back while the running-profile form has unsaved changes (2026-10-20): its own history
  // entry (created by the edit itself, so it carries a user activation) shows the EXISTING confirmation.
  // "Stay" keeps every value — the entry is re-armed on the next interaction, so a later Back asks again;
  // "leave" goes Home directly (onBack, no second confirmation) and the edits are discarded only then.
  useBackEntry(dirty, 'guard', () => {
    if (!window.confirm(LEAVE_PROFILE_MSG)) return 'stay';
    if (onBack) onBack();
    return 'done';
  });
  const cancelEdits = () => {
    const saved = savedFormRef.current;
    if (!saved) return;
    setRunningData(saved.running);
    setCyclingData(saved.cycling);
    setSportsActive(saved.sports);
    touchedFieldsRef.current = new Set();   // the edits were discarded — nothing was touched any more
  };

  // Track which plan-shaping fields the user TOUCHED during the
  // current form session — i.e. ever had a value different from the
  // baseline (last saved). Sticky: we do NOT remove a field when the
  // user reverts to baseline, because semantically they did edit.
  // This is what lets the toast still fire when the flow is
  // baseline → invalid (blocked) → fix-to-baseline → save: the
  // net delta is zero but the user did interact with the field.
  // Cleared after every successful save (see persistAllProfiles).
  useEffect(() => {
    const baseline = lastSavedSnapshotRef.current;
    if (!baseline) return; // pre-load: nothing to compare against
    const cur = {
      avg_weekly_km:  runningData.avg_weekly_km || '',
      goal_distance:  runningData.goal_distance || '',
      level:          runningData.level || '',
      preferred_days: runningData.preferred_days || '',
      goal_time:      runningData.goal_time || '',
      // Plan-shaping fields added 2026-06-10 (user reported these
      // didn't trigger regen even though they materially change the
      // plan output).
      preferred_long_run_day: runningData.preferred_long_run_day || '',
      injury_prone_areas:     runningData.injury_prone_areas || '',
      goal_date:              runningData.goal_date || '',
      goal_race:              runningData.goal_race || '',
      max_hr:                 runningData.max_hr ? String(runningData.max_hr) : '',
      pr_5k:                  runningData.pr_5k || '',
      pr_10k:                 runningData.pr_10k || '',
      pr_half:                runningData.pr_half || '',
      pr_full:                runningData.pr_full || '',
      // Injury v2.
      injury_state:           runningData.injury_state || 'ok',
      injury_discipline:      runningData.injury_discipline || '',
      chronic_sensitivities:  runningData.chronic_sensitivities || '',
    };
    const touched = touchedFieldsRef.current;
    for (const k of Object.keys(cur)) {
      if (String(cur[k] || '') !== String(baseline[k] || '')) {
        touched.add(k); // sticky add — never auto-removes
      }
    }
  }, [runningData]);

  // Cycling + sports-active touched detection. Mirrors the running
  // useEffect above but watches the cycling-plan-shaping fields and
  // the sportsActive toggle. Without this the regenerate-toast (and
  // therefore the global progress-bar banner) never fires when only
  // cycling fields change — user reported 2026-06-04 that adding a
  // cycling profile produced no notification at all.
  useEffect(() => {
    const baseline = lastSavedSnapshotRef.current;
    if (!baseline) return;
    const sortedSports = (sportsActive || [])
      .map(s => String(s).trim())
      .filter(Boolean)
      .sort()
      .join(',');
    const cur = {
      sports_active:           sortedSports,
      weekly_cycling_hours:    cyclingData.weekly_cycling_hours ? String(cyclingData.weekly_cycling_hours) : '',
      preferred_cycling_days:  cyclingData.preferred_cycling_days || '',
      cycling_level:           cyclingData.cycling_level || '',
      cycling_goal_event:      cyclingData.cycling_goal_event || '',
      cycling_goal_date:       cyclingData.cycling_goal_date || '',
      cycling_goal_distance:   cyclingData.cycling_goal_distance ? String(cyclingData.cycling_goal_distance) : '',
      recent_longest_ride_km:  cyclingData.recent_longest_ride_km ? String(cyclingData.recent_longest_ride_km) : '',
    };
    const touched = touchedFieldsRef.current;
    for (const k of Object.keys(cur)) {
      if (String(cur[k] || '') !== String(baseline[k] || '')) {
        touched.add(k);
      }
    }
  }, [cyclingData, sportsActive]);

  const handleSportToggle = (sport) => {
    setSportsActive(prev => {
      if (prev.includes(sport)) { if (prev.length === 1) return prev; return prev.filter(s => s !== sport); }
      return [...prev, sport];
    });
  };

  // Cycling days toggle
  const handleCyclingDayToggle = (day) => {
    const currentDays = cyclingData.preferred_cycling_days ? cyclingData.preferred_cycling_days.split(' ').filter(d => d) : [];
    let newDays;
    if (currentDays.includes(day)) {
      newDays = currentDays.filter(d => d !== day);
    } else {
      newDays = [...currentDays, day];
    }
    const dayOrder = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    newDays.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
    setCyclingData({...cyclingData, preferred_cycling_days: newDays.join(' ')});
  };

  const isCyclingDaySelected = (day) => {
    if (!cyclingData.preferred_cycling_days) return false;
    return cyclingData.preferred_cycling_days.split(' ').includes(day);
  };

  // Running days toggle
  const handleRunningDayToggle = (day) => {
    const currentDays = runningData.preferred_days ? runningData.preferred_days.split(' ').filter(d => d) : [];
    let newDays;
    if (currentDays.includes(day)) {
      newDays = currentDays.filter(d => d !== day);
    } else {
      newDays = [...currentDays, day];
    }
    const dayOrder = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    newDays.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
    // Cross-field guard (2026-06-08): preferred_long_run_day MUST be
    // a subset of preferred_days. If the user just deselected the day
    // that is currently the long-run day, clear long_run_day so the
    // backend doesn't reject the plan with 'long run day empty'.
    const newPrefDays = newDays.join(' ');
    const _cur_long = runningData.preferred_long_run_day;
    const _patch = { preferred_days: newPrefDays };
    if (_cur_long && !newDays.includes(_cur_long)) {
      _patch.preferred_long_run_day = '';
    }
    setRunningData({ ...runningData, ..._patch });
  };

  const isRunningDaySelected = (day) => {
    if (!runningData.preferred_days) return false;
    return runningData.preferred_days.split(' ').includes(day);
  };

  // One save at a time (Profile V2 fix, 2026-10-19). The in-flight flag is set here and released in ONE
  // finally, whatever way the attempt ends — a validation alert / modal, an API error, an exception or a
  // completed save — so a corrected form can always be saved again. Previously only persistAllProfiles
  // released it, and every validation stop left the save button dead until a reload.
  const saveAllProfiles = async () => {
    if (_savingInFlightRef.current) return;   // a click while a save is running is ignored, as before
    _savingInFlightRef.current = true;
    try {
      await runSaveAllProfiles();
    } finally {
      _savingInFlightRef.current = false;
    }
  };
  // Same guard for the paths that persist directly after a modal ("continue anyway").
  const persistGuarded = async () => {
    if (_savingInFlightRef.current) return;
    _savingInFlightRef.current = true;
    try {
      await persistAllProfiles();
    } finally {
      _savingInFlightRef.current = false;
    }
  };

  // The existing save flow, unchanged: validation, modals, the two PUTs, the regenerate question.
  const runSaveAllProfiles = async () => {
    // pain -> ok (owner 2026-11-01): the gradual-return confirmation first, only for this transition. "ביטול" saves
    // nothing and puts the injury state (and the fields choosing "כשיר" cleared) back as saved.
    const _savedRun = (savedFormRef.current && savedFormRef.current.running) || {};
    if (_savedRun.injury_state === 'pain' && (runningData.injury_state || 'ok') === 'ok') {
      const ok = await openSaveDialog(PAIN_EXIT_DIALOG);
      if (!ok) {
        setRunningData((prev) => ({
          ...prev,
          injury_state: _savedRun.injury_state,
          injury_start_date: _savedRun.injury_start_date ?? prev.injury_start_date,
          injury_discipline: _savedRun.injury_discipline ?? prev.injury_discipline,
          injury_description: _savedRun.injury_description ?? prev.injury_description,
        }));
        return;
      }
    }
    // Required fields — only validate running fields when running is in
    // the active sports list. 2026-06-11 bug: cycling-only athletes were
    // blocked from saving with "missing running goal_distance" alerts.
    const _isRunningActive = Array.isArray(sportsActive)
      ? sportsActive.includes('running')
      : true;  // legacy single-sport (no sportsActive array) = running default
    // The running rules below (required fields, km x days, the plan engine's answer) live in
    // utils/runningSaveRules.js — the SAME sequence Onboarding runs before it saves the goal.
    const missing = _isRunningActive ? runningMissingFields(runningData) : [];
    if (missing.length > 0) {
      showBlock(runningMissingMessage(missing));
      return;
    }

    // No overridable weekly-volume warning (owner 2026-11-02, P0): "שמור בכל זאת" used to save with
    // goal_override_acknowledged, the plan engine never reads it and refused the plan. The weekly-km floor (by goal
    // AND level) is now a hard rule, answered by the engine itself below, before anything is saved.

    // Cross-field volume × days check. Same table the coach view uses
    // (see validateKmDaysMatrix). When the combo fails, open the
    // LoadBalanceModal instead of saving — coach spec 2026-06-01:
    // a validation block deserves a modal, not a toast or alert (fix only, no override).
    const _daysCount = runningDaysCount(runningData.preferred_days);
    {
      // Distance-scoped: for 10K the gate is the runner-level band + profile
      // envelope (validateProfile10K); other distances keep the legacy matrix.
      const _matrixCheck = runningMatrixCheck(runningData);
      if (!_matrixCheck.ok) {
        // 10K failures carry a precise level/envelope reason that the days-only
        // LoadBalanceModal cannot express — surface it directly and block save.
        if (_matrixCheck.errorCode) {
          showBlock(`${_matrixCheck.reason}`);
          return;
        }
        setLoadBalanceModal({
          open: true,
          weeklyKm: runningData.avg_weekly_km,
          trainingDays: _daysCount,
          requiredDays: _matrixCheck.requiredDays,
        });
        return;
      }
    }

    // The plan engine's own answer (GET /policy/running-availability — the marathon policy, the weekly-km floor by
    // goal and level, the 5K / 10K envelopes), BEFORE anything is saved; PUT /profile enforces the same answer —
    // both only when the save CHANGES a plan field (a stored profile that predates the rule still saves the rest).
    if (_isRunningActive && runningPlanChanged(savedFormRef.current && savedFormRef.current.running, runningData)) {
      const _plan = await runningPlanCheck(runningData, { fetcher: authFetch, apiBase: API_BASE_URL });
      if (_plan.block) {
        showBlock(_plan.block);
        return;
      }
    }

    // Date validation: goal_date / cycling_goal_date must be today or future.
    const isPastDate = (iso) => {
      if (!iso) return false;
      const d = new Date(iso + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d < today;
    };
    const pastDateLabels = [];
    if (isPastDate(runningData.goal_date)) pastDateLabels.push('תאריך מרוץ ריצה');
    if (isPastDate(cyclingData.cycling_goal_date)) pastDateLabels.push('תאריך מרוץ רכיבה');
    if (pastDateLabels.length > 0) {
      showBlock(`תאריך מרוץ לא יכול להיות בעבר:\n• ${pastDateLabels.join('\n• ')}\n\nעדכן את התאריך להיום או לעתיד.`);
      return;
    }

    // Pair-coherence: race name + race date are both OPTIONAL, but if
    // one is filled the other must be too. Both-empty (no race goal)
    // and both-filled (real race goal) are valid; one-empty-one-filled
    // is a half-set state that would confuse the plan generator.
    const pairErrors = [];
    const runRaceFilled  = !!(runningData.goal_race && runningData.goal_race.trim());
    const runDateFilled  = !!(runningData.goal_date && runningData.goal_date.trim());
    if (runRaceFilled !== runDateFilled) {
      pairErrors.push(
        runRaceFilled
          ? 'מילאת שם מרוץ ריצה ללא תאריך — הזן תאריך או מחק את שם המרוץ'
          : 'מילאת תאריך מרוץ ריצה ללא שם — הזן שם מרוץ או מחק את התאריך',
      );
    }
    const cycRaceFilled  = !!(cyclingData.cycling_goal_event && cyclingData.cycling_goal_event.trim());
    const cycDateFilled  = !!(cyclingData.cycling_goal_date && cyclingData.cycling_goal_date.trim());
    if (cycRaceFilled !== cycDateFilled) {
      pairErrors.push(
        cycRaceFilled
          ? 'מילאת שם מרוץ רכיבה ללא תאריך — הזן תאריך או מחק את שם המרוץ'
          : 'מילאת תאריך מרוץ רכיבה ללא שם — הזן שם מרוץ או מחק את התאריך',
      );
    }
    if (pairErrors.length > 0) {
      showBlock(`שדות מרוץ לא עקביים:\n• ${pairErrors.join('\n• ')}`);
      return;
    }

    // Phase 1 cycling mandatory: level + days + hours only.
    if (sportsActive.includes('cycling')) {
      const missing = [];
      if (!cyclingData.cycling_level) missing.push('רמת רוכב');
      if (!cyclingData.preferred_cycling_days || cyclingData.preferred_cycling_days.split(' ').filter(d => d).length === 0) missing.push('ימי רכיבה מועדפים');
      if (!cyclingData.weekly_cycling_hours || parseFloat(cyclingData.weekly_cycling_hours) <= 0) missing.push('שעות שבועיות');
      // Distance is required unless user explicitly checked "no race".
      if (cyclingData.has_cycling_race !== false && !cyclingData.cycling_goal_distance) missing.push('מרחק תחרות (60 / 100 / 160 ק"מ)');
      if (cyclingData.has_cycling_race !== false && !cyclingData.cycling_goal_event?.trim()) missing.push('שם אירוע תחרות');
      if (cyclingData.has_cycling_race !== false && !cyclingData.cycling_goal_date) missing.push('תאריך תחרות');
      if (missing.length > 0) {
        showBlock(`חסרים שדות חובה בפרופיל רכיבה:\n• ${missing.join('\n• ')}`);
        return;
      }
    }

    // Goal guardrail — running goal only. Cycling has its own dynamics.
    // The validator's normalizeDistance accepts both raw value forms
    // ('5'/'10'/'21.1'/'42.2') and Hebrew labels ('5K'/'חצי מרתון'/etc).
    const weeklyKm = parseFloat(runningData.avg_weekly_km);
    const profileResult = validateUserGoal(weeklyKm, runningData.goal_distance, runningData.goal_time);

    // Cross-check against the LONGEST upcoming race in races[]. Bug
    // we saw 2026-05-31: athlete with a marathon 27 days out dropped
    // avg_weekly_km to 30 and the profile guardrail stayed silent
    // because profile.goal_distance was 5K. The race calendar carries
    // the real obligation — if it says marathon, 30 km/week is danger
    // territory regardless of what the profile field happens to hold.
    let raceResult = null;
    let longestRace = null;
    try {
      const racesRes = await authFetch(`${API_BASE_URL}/races?user_id=${userId}`);
      if (racesRes.ok) {
        const racesPayload = await racesRes.json();
        const racesList = racesPayload.races || racesPayload || [];
        const today = new Date(); today.setHours(0, 0, 0, 0);
        // Distance rank: marathon > half > 10K > 5K. We only care about
        // upcoming races within ~90 days — anything further is a long-
        // term goal, not a current-cycle obligation.
        const distRank = (raw) => {
          const d = String(raw || '').toLowerCase();
          if (d.includes('מרתון') && !d.includes('חצי')) return 4;
          if (d.includes('marathon') && !d.includes('half')) return 4;
          if (d === '42' || d === '42.2') return 4;
          if (d.includes('חצי') || d.includes('half') || d === '21' || d === '21.1') return 3;
          if (d === '10' || d.includes('10k') || d === '10km') return 2;
          if (d === '5'  || d.includes('5k')  || d === '5km')  return 1;
          return 0;
        };
        const upcoming = racesList
          .filter(r => r.race_date && new Date(r.race_date + 'T00:00:00') >= today)
          .filter(r => {
            const dt = new Date(r.race_date + 'T00:00:00');
            const days = Math.round((dt - today) / 86400000);
            return days <= 120;
          });
        if (upcoming.length) {
          upcoming.sort((a, b) => distRank(b.distance) - distRank(a.distance));
          const top = upcoming[0];
          if (distRank(top.distance) > distRank(runningData.goal_distance)) {
            longestRace = top;
            raceResult = validateUserGoal(weeklyKm, top.distance, top.target_time);
          }
        }
      }
    } catch (e) {
      console.warn('Race-aware guardrail failed (will use profile-only):', e);
    }

    // Take the STRICTEST of the two checks.
    const rank = { ok: 0, warning: 1, critical: 2 };
    const result = raceResult && rank[raceResult.status] > rank[profileResult.status]
      ? raceResult
      : profileResult;
    if (result.status !== 'ok') {
      // When the guardrail fires we KNOW the user is editing a plan-
      // shaping field (otherwise the validator wouldn't trip). Mark
      // those fields as touched so the regenerate toast still fires
      // after the 'continue anyway' path — even when the user clicked
      // 'not now' on a previous toast and then re-saved the form
      // without literally typing anything in between. User report
      // 2026-05-31: 'I saved again, got the warning, continued — no
      // toast'. Sticky touched fixes it because the guardrail itself
      // is now a touch-signal source.
      touchedFieldsRef.current.add('avg_weekly_km');
      if (result.distance)    touchedFieldsRef.current.add('goal_distance');
      if (result.targetTime)  touchedFieldsRef.current.add('goal_time');
      setGuardrail({ result, raceContext: longestRace });
      return;
    }

    await persistAllProfiles();
  };

  const persistAllProfiles = async () => {
    // Hard guard (2026-06-08): when an injury / rehab state is set,
    // injury_sport is REQUIRED. Without it the backend validator
    // Dual-sport users with active injury must pick a discipline.
    if (
      runningData.injury_state !== 'ok' &&
      sportsActive.length > 1 &&
      !runningData.injury_discipline
    ) {
      try { showToast && showToast('בחר/י ספורט מושפע — חובה כשמצוין פציעה', 'error', 4500); } catch (_e) {}
      showBlock('לא ניתן לשמור — נא לבחור איזה ספורט מושפע מהפציעה.');
      return;
    }
    // P0 2026-10-30 / owner rule 2026-10-31: a real change of a plan input (days, volume, goal, goal time, level,
    // long-run day) rebuilds the four coming weeks from MASTER and overwrites earlier changes — asked BEFORE the save;
    // "ביטול" saves nothing. The question only: the rebuild itself follows the server's decision after the save.
    const _rb = planRebuildChange(savedFormRef.current && savedFormRef.current.running, runningData);
    if (_rb.settings) {
      const _go = await openSaveDialog({ kind: 'confirm', title: 'בנייה מחדש של התכנית', message: REBUILD_WARNING,
                                         confirmLabel: REBUILD_CONFIRM_LABEL });
      if (!_go) return;
    }
    try {
      setSaving(true);

      // Save cycling profile
      // ✅ Include user_id in body as per CLAUDE.md requirement
      const cyclingPayload = {
        user_id: userId,
        sports_active: sportsActive.join(','),
        // Phase 1: road discipline hardcoded.
        discipline: 'road',
        bike_types: 'road',
        weekly_cycling_hours: cyclingData.weekly_cycling_hours ? parseFloat(cyclingData.weekly_cycling_hours) : null,
        cycling_goal_event: cyclingData.cycling_goal_event || null,
        cycling_level: cyclingData.cycling_level || null,
        cycling_goal_date: cyclingData.cycling_goal_date || null,
        cycling_goal_distance: cyclingData.cycling_goal_distance || null,
        has_race: cyclingData.has_cycling_race === false ? false : (cyclingData.has_cycling_race === true ? true : null),
        preferred_cycling_days: cyclingData.preferred_cycling_days || null,
        max_hr: cyclingData.max_hr ? parseInt(cyclingData.max_hr) : null,
        recent_longest_ride_km: cyclingData.recent_longest_ride_km ? parseFloat(cyclingData.recent_longest_ride_km) : null,
      };
      const _cycRes = await authFetch(`${API_BASE_URL}/profile/cycling?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cyclingPayload)
      });
      // Check for validation errors (e.g. cycling combo gate 400).
      // Without this the user saw "saved successfully" while the
      // server actually blocked it — bug 2026-06-10.
      if (!_cycRes.ok) {
        let _errMsg = 'שגיאה בשמירת פרופיל רכיבה';
        try {
          const _errBody = await _cycRes.json();
          // FastAPI wraps detail in either string or {error,message} dict.
          if (_errBody?.detail?.message) _errMsg = _errBody.detail.message;
          else if (typeof _errBody?.detail === 'string') _errMsg = _errBody.detail;
          else if (_errBody?.message) _errMsg = _errBody.message;
        } catch {}
        showBlock(_errMsg, 'השמירה נכשלה');
        return;  // STOP the save flow — do not proceed to running save.
      }

      // Save running profile (to main profile)
      // ✅ Include user_id in body as per CLAUDE.md requirement
      const runningPayload = {
        user_id: userId,
        level: runningData.level || null,
        goal_race: runningData.goal_race || null,
        goal_distance: runningData.goal_distance || null,
        goal_date: runningData.goal_date || null,
        goal_time: runningData.goal_time || null,
        avg_weekly_km: runningData.avg_weekly_km ? parseInt(runningData.avg_weekly_km) : null,
        // max_hr is sport-agnostic — only sent when actually entered on
        // the running side. Otherwise the running PUT would overwrite a
        // valid max_hr saved by the cycling PUT (bug 2026-06-10 — user
        // entered max_hr=185 in cycling, then save flow ran cycling PUT
        // (saved 185) then running PUT (sent null) → overwrite to null).
        ...(runningData.max_hr ? { max_hr: parseInt(runningData.max_hr) } : {}),
        preferred_days: runningData.preferred_days || null,
        preferred_long_run_day: runningData.preferred_long_run_day || null,
        training_days_per_week: runningData.preferred_days ? runningData.preferred_days.split(' ').filter(d => d).length : null,
        injury_prone_areas: runningData.injury_prone_areas || null,
        // Injury v2 fields. Always null out legacy fields so skeletons
        // that check active_injury_severity don't block plan generation.
        injury_state:           runningData.injury_state !== 'ok' ? runningData.injury_state : null,
        injury_discipline:      runningData.injury_discipline || null,
        injury_description:     runningData.injury_description || null,
        injury_start_date:      runningData.injury_start_date || null,
        chronic_sensitivities:  runningData.chronic_sensitivities || null,
        active_injury_severity: null,
        injury_sport:           null,
        rehab_status:           null,
        rehab_week:             null,
        // Never an override for running (owner 2026-11-02): the server refuses a profile the engine cannot build
        // either way, and a confirmed goal-time warning must not skip the weekly-km floor.
        goal_override_acknowledged: false,
      };
      console.log('📝 Saving running profile:', runningPayload);
      const runningResponse = await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(runningPayload)
      });
      const runningResult = await runningResponse.json();
      if (!runningResponse.ok) {
        // FastAPI/Pydantic 422 → detail is an ARRAY of validation errors: the first message is shown (without the
        // "Value error," prefix / the developer override hint). (User 2026-07-24.)
        showBlock(saveErrorMessage(runningResult), 'השמירה נכשלה');
        return;
      }
      console.log('✅ Running profile saved:', runningResult);
      markSaved(runningData, cyclingData, sportsActive);   // the saved values are the new baseline

      if (onUpdate) onUpdate();

      // ── Plan-shaping touched detection ────────────────────────
      // Use the sticky touched set instead of a raw before/after
      // diff. Reason: user-reported bug where save after the flow
      // baseline=35 → tried 29 (hard-blocked) → fixed back to 35 →
      // save produced no toast because the net delta was zero.
      // touchedFieldsRef captures every field whose value differed
      // from baseline at any point during the session, so the toast
      // fires whenever the user actually interacted with a plan-
      // shaping field — not only when the saved value differs.
      const newSnap = {
        avg_weekly_km:  runningData.avg_weekly_km || '',
        goal_distance:  runningData.goal_distance || '',
        level:          runningData.level || '',
        preferred_days: runningData.preferred_days || '',
        goal_time:      runningData.goal_time || '',
        // Injury v2 (2026-06-25).
        injury_state:           runningData.injury_state || 'ok',
        injury_discipline:      runningData.injury_discipline || '',
        chronic_sensitivities:  runningData.chronic_sensitivities || '',
        // Cycling fields.
        sports_active:           (sportsActive || []).map(s => String(s).trim()).filter(Boolean).sort().join(','),
        weekly_cycling_hours:    cyclingData.weekly_cycling_hours ? String(cyclingData.weekly_cycling_hours) : '',
        preferred_cycling_days:  cyclingData.preferred_cycling_days || '',
        cycling_level:           cyclingData.cycling_level || '',
        cycling_goal_event:      cyclingData.cycling_goal_event || '',
        cycling_goal_date:       cyclingData.cycling_goal_date || '',
        cycling_goal_distance:   cyclingData.cycling_goal_distance ? String(cyclingData.cycling_goal_distance) : '',
        recent_longest_ride_km:  cyclingData.recent_longest_ride_km ? String(cyclingData.recent_longest_ride_km) : '',
      };
      const FIELD_LABELS_HE = {
        avg_weekly_km:  'ק״מ שבועי',
        goal_distance:  'מרחק יעד',
        goal_time:      'זמן מטרה',
        level:          'רמה',
        preferred_days: 'ימי אימון',
        // Plan-shaping fields added 2026-06-10.
        preferred_long_run_day: 'יום ריצה ארוכה',
        injury_prone_areas:     'אזורי פציעה',
        goal_date:              'תאריך מרוץ יעד',
        goal_race:              'שם מרוץ יעד',
        max_hr:                 'דופק מקסימלי',
        pr_5k:                  'שיא 5K',
        pr_10k:                 'שיא 10K',
        pr_half:                'שיא חצי',
        pr_full:                'שיא מרתון',
        // Injury v2 labels.
        injury_state:           'מצב פציעה',
        injury_discipline:      'ספורט מושפע',
        chronic_sensitivities:  'רגישויות כרוניות',
        // Cycling labels.
        sports_active:           'ספורט פעיל',
        weekly_cycling_hours:    'שעות רכיבה שבועיות',
        preferred_cycling_days:  'ימי רכיבה',
        cycling_level:           'רמת רכיבה',
        cycling_goal_event:      'אירוע רכיבה מטרה',
        cycling_goal_date:       'תאריך אירוע רכיבה',
        cycling_goal_distance:   'מרחק תחרות רכיבה',
        recent_longest_ride_km:  'רכיבה ארוכה אחרונה',
      };
      // Touched detection: union the useEffect-populated ref with a
      // FRESH snapshot-vs-baseline diff. The useEffect-only path
      // missed first-save bugs (user reported 2026-06-06): if the
      // useEffect fired before baseline was set OR if a state cascade
      // (parent fetchProfile via onUpdate) re-ran without populating
      // the ref, the touched set was empty and no regenerate toast
      // appeared. Computing the diff here at save-time, against the
      // PRE-update baseline (`prevBaseline`), is the safety net —
      // any field the user actually changed is detected regardless
      // of effect timing.
      const prevBaseline = lastSavedSnapshotRef.current || {};
      const touchedNow = new Set(touchedFieldsRef.current);
      for (const k of Object.keys(newSnap)) {
        if (String(newSnap[k] || '') !== String(prevBaseline[k] || '')) {
          touchedNow.add(k);
        }
      }
      const changedFields = Array.from(touchedNow)
        .map(k => FIELD_LABELS_HE[k])
        .filter(Boolean);
      const planFieldChanged = changedFields.length > 0;
      lastSavedSnapshotRef.current = newSnap;
      touchedFieldsRef.current.clear();

      // The SERVER decides whether this save requires the rebuild (rebuild-status, not the local diff): required ->
      // the four weeks are rebuilt now, no second question. Unreadable status -> the local diff decides (the rebuild
      // helper reads the status again and does nothing when nothing is required).
      let _required = _rb.settings || _rb.injury;
      try {
        _required = !!(await getRebuildStatus(userId)).required;
      } catch (_e) { /* keep the local answer */ }
      if (_required) {
        runProfileChangeRebuild(_rb);
      } else if (planFieldChanged) {
        // Derive the touched sports HERE so the toast can show the
        // exact quota cost up front. Same labels as
        // handleConfirmRegenerate — keep them in sync if you add a
        // new field category.
        const _RUN_LABELS_S = new Set([
          'ק"מ שבועי', 'ק״מ שבועי', 'מרחק יעד', 'זמן מטרה', 'רמה', 'ימי אימון',
          // 2026-06-10 — plan-shaping fields that previously didn't trigger regen.
          'יום ריצה ארוכה', 'אזורי פציעה',
          'תאריך מרוץ יעד', 'שם מרוץ יעד', 'דופק מקסימלי',
          'שיא 5K', 'שיא 10K', 'שיא חצי', 'שיא מרתון',
        ]);
        const _CYC_LABELS_S = new Set([
          'שעות רכיבה שבועיות',
          'ימי רכיבה', 'רמת רכיבה',
          'אירוע רכיבה מטרה', 'תאריך אירוע רכיבה', 'מרחק תחרות רכיבה', 'רכיבה ארוכה אחרונה',
        ]);
        // SHARED fields — affect plan structure of EVERY active sport.
        // Moved out of _RUN_LABELS_S 2026-06-10: strength is physiologically
        // shared between running and cycling, so a change must regenerate
        // both sports the athlete actually trains (filtered by _activeSports
        // below). Same rule as injury fields.
        const _SHARED_LABELS_S = new Set([
        ]);
        // Injury state → regenerate all active sports with volume modifier.
        const _INJURY_LABELS_S = new Set(['מצב פציעה', 'ספורט מושפע']);
        const _activeSports = (sportsActive && sportsActive.length) ? sportsActive : ['running'];
        const _touched = new Set();
        for (const lbl of changedFields) {
          if (_RUN_LABELS_S.has(lbl)) _touched.add('running');
          if (_CYC_LABELS_S.has(lbl)) _touched.add('cycling');
          if (_SHARED_LABELS_S.has(lbl)) _activeSports.forEach(s => _touched.add(s));
          if (_INJURY_LABELS_S.has(lbl)) _activeSports.forEach(s => _touched.add(s));
          if (lbl === 'ספורט פעיל') _activeSports.forEach(s => _touched.add(s));
        }
        let _previewSports = [..._touched].filter(s => _activeSports.includes(s));
        if (!_previewSports.length) _previewSports = _activeSports;
        const _cost = _previewSports.length * 2; // 2 weeks per sport

        // Read cached message-usage (populated by App.js loadMessageUsage).
        // Best-effort — if the cache isn't there, we still show the
        // toast without exact numbers and the server-side quota guard
        // will block the regen if the user is over.
        let _remaining = null;
        let _dailyLimit = null;
        try {
          const _cached = localStorage.getItem(`message_usage_${userId}`);
          if (_cached) {
            const _u = JSON.parse(_cached);
            _remaining = typeof _u.remaining === 'number' ? _u.remaining : null;
            _dailyLimit = typeof _u.daily_limit === 'number' ? _u.daily_limit : null;
          }
        } catch (_) {}

        // Ask the backend whether the 24h rate limit allows this. We
        // never trust the client to enforce — server is the source of
        // truth — but we DO let the response shape the UI text.
        try {
          const rateRes = await authFetch(`${API_BASE_URL}/profile/can-regenerate?user_id=${userId}`);
          const rate = await rateRes.json();
          const _quotaBase = {
            cost: _cost,
            remaining: _remaining,
            daily_limit: _dailyLimit,
            target_sports: _previewSports,
          };
          if (rate.can_regenerate) {
            setRegenerateToast({ mode: 'ask', hoursRemaining: 0, changedFields, ..._quotaBase });
          } else {
            setRegenerateToast({ mode: 'blocked', hoursRemaining: rate.hours_remaining || 1, changedFields, ..._quotaBase });
          }
        } catch (e) {
          console.warn('can-regenerate check failed (will allow toast):', e);
          setRegenerateToast({ mode: 'ask', hoursRemaining: 0, changedFields, cost: _cost, remaining: _remaining, daily_limit: _dailyLimit, target_sports: _previewSports });
        }
      }
    } catch (err) { console.error('Error saving profiles:', err); }
    finally { setSaving(false); }   // the in-flight flag is released by saveAllProfiles / persistGuarded
  };

  // The rebuild after a real profile change (utils/planRebuild): one clear message at the end — success, or a real
  // error saying the plan was NOT updated (the previous plan stays as it was).
  const runProfileChangeRebuild = async (rb) => {
    const reason = rb.injury && !rb.settings ? 'injury' : 'settings';
    showToast(reason === 'injury' ? 'מעדכן את התכנית למצב הפציעה החדש…' : 'מעדכן את התכנית לפי הפרופיל…', 'info', 4000);
    let out;
    try {
      out = await runPlanRebuild(userId, { reason });
    } catch (_e) {
      out = { ok: false, kind: 'failed', message: 'עדכון התכנית נכשל. התכנית הקודמת נשארה ואינה מעודכנת — אפשר לנסות שוב.' };
    }
    if (out.kind === 'none') return;
    showToast(out.message, out.ok ? 'success' : 'error', out.ok ? 5000 : 9000);
  };

  // Toast handlers — "Yes regenerate" hits the SERVER-SIDE batch
  // endpoint /weekly-plan/regenerate-all. The backend loops
  // sports × (current, next) sequentially, and we only poll ONE
  // job_id. F5-safe: refreshing the page mid-batch re-attaches to
  // the same job via resumeInFlightPlanGeneration in App.js.
  //
  // Previous flow (pre-2026-06-04) fired 4 sequential generate-v2
  // calls from the browser — an F5 in the middle silently killed
  // the remaining iterations, so e.g. cycling plans never got
  // regenerated even though running ones did. User report:
  // 'הורדתי 4 שעות רכיבה — תכנית רכיבה לא עודכנה'.
  //
  // Events: we still dispatch plan-regen-start / plan-regen-done on
  // window so the global App.js banner shows up — the helper
  // planRegenRequest does it for us via the (non-silent) call below.
  const handleConfirmRegenerate = async () => {
    const changedFields = (regenerateToast && regenerateToast.changedFields) || [];
    setRegenerateToast({ mode: 'generating', hoursRemaining: 0, changedFields });

    // Touched-based sport selection (2026-06-05): only regenerate the
    // sport(s) the user actually changed fields for. Previously the
    // batch always covered BOTH sports × current + next = 4 plans
    // = ~5.5 minutes even when only one cycling field moved. With
    // this filter, a cycling-only change runs cycling × current+next
    // = 2 plans = ~2.7 minutes. The touched ref is the same one
    // populated by the running + cycling useEffects above; we read
    // it once here BEFORE the batch fires (it's cleared inside the
    // persistAllProfiles save, but only on next save — handleConfirmRegenerate
    // fires from the toast after that save, so the ref is empty here
    // unless persistAllProfiles repopulated it… it doesn't. So we
    // derive sports from the changedFields labels instead, which are
    // the Hebrew labels the toast was built from). FIELD_LABELS_HE
    // is the inverse map in persistAllProfiles.
    const _RUN_LABELS = new Set([
      'ק"מ שבועי', 'ק״מ שבועי', 'מרחק יעד', 'זמן מטרה', 'רמה', 'ימי אימון',
      // 2026-06-10 — keep in sync with _RUN_LABELS_S above. Strength
      // fields ('אימון כוח', 'ימי כוח') moved to _SHARED_LABELS below
      // because they affect BOTH sports physiologically (rest day,
      // load distribution) — same rule as injury fields.
      'יום ריצה ארוכה', 'אזורי פציעה',
      'תאריך מרוץ יעד', 'שם מרוץ יעד', 'דופק מקסימלי',
      'שיא 5K', 'שיא 10K', 'שיא חצי', 'שיא מרתון',
    ]);
    // SHARED — affect EVERY active sport. Mirror of _SHARED_LABELS_S above.
    const _SHARED_LABELS = new Set([
      'אימון כוח', 'ימי כוח',
    ]);
    const _CYC_LABELS = new Set([
      'שעות רכיבה שבועיות', 'ק״מ רכיבה שבועי', 'ק"מ רכיבה שבועי',
      'ימי רכיבה', 'רמת רכיבה', 'סוג אופניים',
      'אירוע רכיבה מטרה', 'תאריך אירוע רכיבה', 'רכיבה ארוכה אחרונה',
    ]);
    // Injury state → regenerate all active sports with volume modifier applied by skeleton.
    const _INJURY_LABELS = new Set(['מצב פציעה', 'ספורט מושפע']);
    const _activeSports = (sportsActive && sportsActive.length) ? sportsActive : ['running'];
    const _touchedSports = new Set();
    for (const lbl of changedFields) {
      if (_RUN_LABELS.has(lbl)) _touchedSports.add('running');
      if (_CYC_LABELS.has(lbl)) _touchedSports.add('cycling');
      if (_SHARED_LABELS.has(lbl)) _activeSports.forEach(s => _touchedSports.add(s));
      if (_INJURY_LABELS.has(lbl)) _activeSports.forEach(s => _touchedSports.add(s));
      // sports_active toggle — assume newly-added sport needs a plan.
      if (lbl === 'ספורט פעיל') _activeSports.forEach(s => _touchedSports.add(s));
    }
    // Filter to sports the user is actually active in (toggle could
    // have dropped one). Fallback: if no field categorized, regenerate
    // every active sport — safer than skipping entirely.
    let targetSports = [..._touchedSports].filter(s => _activeSports.includes(s));
    if (!targetSports.length) targetSports = _activeSports;
    console.log(`🎯 Regenerate target sports (touched-based): ${targetSports.join(', ')} (from changedFields: ${changedFields.join(', ')})`);
    try {
      // ONE call. The backend's BG worker handles sports×weeks
      // sequentially so the CPU never has to chew on two Gemini
      // Pro requests at once. planRegenRequest does the 202+poll
      // dance + dispatches the banner events. Result.success is
      // true if at least one child plan was saved.
      const result = await planRegenRequest({
        url: `${API_BASE_URL}/weekly-plan/regenerate-all?user_id=${userId}`,
        body: {
          sports: targetSports,
          weeks: ['current', 'next', 'week_plus2', 'week_plus3'],
          triggered_by_user: true,
          // 2026-06-11: profile changed → MUST regenerate even if a plan
          // already exists. Without force, the skip-existing optimization
          // (designed for repeat clicks of the create button) would silently
          // ignore the regen and the athlete would keep the old plan.
          force: true,
        },
        pollUserId: userId,
        changedFields,
      });
      if (!result.success) {
        throw new Error(`${result.error || 'fail'} for batch`);
      }
      // Skipped (no_op) — backend declined to regen because plans exist
      // and force=false. With force:true on the profile-save path we
      // shouldn't hit this, but defend just in case.
      if (result.skipped || result.status === 'no_op') {
        showToast('ℹ️ התכניות הקיימות עדיין מתאימות — לא נדרשת רגנרציה.', 'info', 4500);
        setRegenerateToast(null);
        return;
      }
      setRegenerateToast({ mode: 'done', hoursRemaining: 0, changedFields });
      setTimeout(() => setRegenerateToast(null), 3000);
      window.dispatchEvent(new CustomEvent('plan-regen-done', { detail: { success: true } }));
      // Big bottom-center toast — the small regenerateToast 'done'
      // chip was easy to miss. User feedback 2026-06-03: "no clear
      // notification that update finished" after a profile-driven
      // regenerate. Toast hangs around 4s so it can't be missed.
      const _sportsLabel = (targetSports || []).length > 1
        ? 'ריצה + רכיבה'
        : (targetSports?.[0] === 'cycling' ? 'רכיבה' : 'ריצה');
      showToast(`תכניות ${_sportsLabel} עודכנו בהצלחה (נוכחי + הבא)!`, 'success', 4500);
    } catch (e) {
      console.error('Regenerate failed:', e);
      setRegenerateToast({
        mode: 'blocked',
        hoursRemaining: null,
        errorMessage: 'לא הצלחתי לייצר תכנית חדשה. אפשר לנסות שוב מאוחר יותר.',
      });
      window.dispatchEvent(new CustomEvent('plan-regen-done', { detail: { success: false, error: String(e) } }));
      showToast('עדכון התכנית נכשל — אפשר לנסות שוב מאוחר יותר', 'error', 5000);
    }
  };

  const handleDismissRegenerateToast = () => setRegenerateToast(null);

  if (loading) return <div className={CARD}><div className="animate-pulse text-text-muted text-center">טוען...</div></div>;

  return (
    <div className="space-y-4">
      {/* Regenerate toast — appears AFTER 'save profile' if any plan-shaping
          field changed. Fixed at the bottom so it doesn't fight with the
          form layout. Backdrop dims the screen but doesn't block input. */}
      {regenerateToast && (
        <div className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4">
          <div className="max-w-lg mx-auto rounded-2xl bg-bg border-2 border-border p-4 sm:p-5">
            {regenerateToast.mode === 'ask' && (() => {
              // Quota transparency block (2026-06-05 spec).
              const _cost = regenerateToast.cost ?? null;
              const _remaining = regenerateToast.remaining;
              const _limit = regenerateToast.daily_limit;
              const _sports = regenerateToast.target_sports || [];
              const _hasQuotaInfo = (typeof _remaining === 'number' && typeof _cost === 'number');
              const _insufficient = _hasQuotaInfo && _remaining < _cost;
              const _sportLabel = _sports.length === 0
                ? ''
                : _sports.length === 2 ? 'ריצה + רכיבה'
                : _sports[0] === 'cycling' ? 'רכיבה' : 'ריצה';
              return (
                <>
                  <p className="text-text text-sm font-semibold mb-2">
                    הפרופיל עודכן בהצלחה!
                  </p>
                  {regenerateToast.changedFields && regenerateToast.changedFields.length > 0 && (
                    <p className="text-text-body text-sm leading-snug mb-2">
                      <span className="font-semibold">ביקשת לעדכן: </span>
                      {regenerateToast.changedFields.join(', ')}
                    </p>
                  )}
                  <p className="text-text-body text-sm leading-snug mb-3">
                    רוצה שהמאמן יבנה תוכנית חדשה {_sportLabel ? `ל${_sportLabel} ` : ''}(נוכחי + הבא) שתואמת את הנתונים החדשים?
                  </p>
                  {_hasQuotaInfo && (
                    // 2026-10-28: #111 box, 1px line border, 13px secondary text; the numbers in the main text colour
                    // (never lime on this sheet — lime marks only the primary action)
                    <div className="rounded-[12px] bg-bg border border-border p-3 mb-3 text-[13px] leading-snug"
                         data-testid="regen-quota">
                      <p className="m-0 text-text-body">
                        קריאות נדרשות: <span className="font-bold text-text">{_cost}</span>
                        {' · '}
                        יתרה: <span className="font-bold text-text">{_remaining}</span>
                        {typeof _limit === 'number' ? <> מתוך <span className="font-bold text-text">{_limit}</span></> : null}
                      </p>
                      {_insufficient ? (
                        <p className="mt-1.5 mb-0 text-text-body">
                          אין מספיק קריאות פנויות. העדכון יתאפשר מחר.
                        </p>
                      ) : (
                        <p className="mt-1.5 mb-0 text-text-body">
                          לאחר העדכון יישארו לך <span className="font-bold text-text">{Math.max(0, _remaining - _cost)}</span> קריאות.
                        </p>
                      )}
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={handleConfirmRegenerate}
                      disabled={_insufficient}
                      data-testid="regen-confirm"
                      className="w-full px-4 btn-primary text-[15px] transition-colors"
                    >
                      {_insufficient ? 'לא ניתן עכשיו' : 'כן, תייצר לי תוכנית מחדש'}
                    </button>
                    <button
                      type="button"
                      onClick={handleDismissRegenerateToast}
                      data-testid="regen-dismiss"
                      className="w-full min-h-[48px] px-4 rounded-[12px] bg-transparent border border-border text-text text-[15px] font-semibold transition-colors"
                    >
                      לא כרגע
                    </button>
                  </div>
                </>
              );
            })()}
            {regenerateToast.mode === 'blocked' && (
              <>
                <p className="text-text text-sm font-semibold mb-2">
                  הפרופיל עודכן
                </p>
                <p className="text-text-body text-sm leading-snug mb-3">
                  {regenerateToast.errorMessage || (
                    <>
                      ניתן לייצר תוכנית מחדש פעם אחת ביום
                      {regenerateToast.hoursRemaining ? (
                        <span> (אפשר שוב בעוד כ-{regenerateToast.hoursRemaining} שעות).</span>
                      ) : '.'}
                      {' '}השינויים ישתקפו אוטומטית בשבוע הבא.
                    </>
                  )}
                </p>
                <button
                  onClick={handleDismissRegenerateToast}
                  className="w-full px-3 py-2 rounded-lg bg-pressed hover:bg-pressed text-text-body text-sm transition-colors"
                >
                  הבנתי
                </button>
              </>
            )}
            {regenerateToast.mode === 'generating' && (
              <div className="flex items-center gap-3 py-1">
                <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                <p className="text-text text-sm">בונה לך תוכנית חדשה לפי הפרופיל המעודכן…</p>
              </div>
            )}
            {regenerateToast.mode === 'done' && (
              <div className="flex items-center gap-3 py-1">
                <CheckCircle size={22} weight="bold" className="text-accent shrink-0" aria-hidden="true" />
                <p className="text-text text-sm">התוכנית עודכנה — בדוק במסך התכנית השבועית.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sport selection — only meaningful with more than one sport (Profile V2: hidden while cycling is
          off; sportsActive keeps its stored value and is saved as before). */}
      {CYCLING_ENABLED && (
        <div className={CARD}>
          <CardTitle>הספורט שלי</CardTitle>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
              <input type="checkbox" checked={sportsActive.includes('running')} onChange={() => handleSportToggle('running')} className="w-5 h-5" />
              <span className="text-text">ריצה</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
              <input type="checkbox" checked={sportsActive.includes('cycling')} onChange={() => handleSportToggle('cycling')} className="w-5 h-5" />
              <span className="text-text">רכיבה</span>
            </label>
          </div>
        </div>
      )}

      {sportsActive.includes('running') && (() => {
        const runDays = runningData.preferred_days ? runningData.preferred_days.split(' ').filter((d) => d) : [];
        const injury = INJURY_OPTIONS.find((o) => o.value === runningData.injury_state) || INJURY_OPTIONS[0];
        const historySummary = [runningData.injury_prone_areas, runningData.chronic_sensitivities]
          .map((v) => (v || '').trim()).filter(Boolean).join(' · ') || 'לא הוזן';
        const zones = hrZones(runningData.max_hr);
        const setInjuryState = (value) => {
          // Same transition rules as before: stamp the start date on ok → injured, clear on recovery.
          const today = new Date().toISOString().slice(0, 10);
          const wasOk = runningData.injury_state === 'ok';
          setRunningData({
            ...runningData,
            injury_state: value,
            injury_start_date: value !== 'ok' && wasOk ? today : runningData.injury_start_date,
            ...(value === 'ok' ? { injury_start_date: null, injury_discipline: '', injury_description: '' } : {}),
          });
        };
        return (
          <>
            {/* 2. האימונים שלי */}
            <section className={CARD} aria-labelledby="pf-training" data-testid="pf-training">
              <CardTitle><span id="pf-training">האימונים שלי</span></CardTitle>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="min-w-0">
                  <label htmlFor="pf-level" className={LABEL}>רמה (לפי ק"מ שבועי)</label>
                  <select id="pf-level" value={runningData.level} onChange={(e) => setRunningData({...runningData, level: e.target.value})}
                          className={`${INPUT} border-border`}>
                    <option value="">בחר רמה...</option>
                    <option value="beginner">מתחיל (עד 25 ק"מ)</option>
                    <option value="experienced">מנוסה (26–40 ק"מ)</option>
                    <option value="advanced">מתקדם (41+ ק"מ)</option>
                  </select>
                </div>
                <div className="min-w-0">
                  <label htmlFor="pf-km" className={LABEL}>ק"מ שבועי <span className="text-quality">*</span></label>
                  <input id="pf-km" type="number" inputMode="numeric" required dir="ltr" value={runningData.avg_weekly_km}
                         aria-describedby="pf-km-guide"
                         onChange={(e) => setRunningData({...runningData, avg_weekly_km: e.target.value})} placeholder="40"
                         className={`${INPUT} text-right ${runningData.avg_weekly_km ? 'border-border' : 'border-quality'}`} />
                </div>
              </div>

              {/* Weekly-km guidance (UI only): what the EXISTING save check will do for this distance /
                  day count / level. Never changes the value and never blocks — saveAllProfiles does. */}
              {(() => {
                const guide = weeklyKmGuidance({ distance: runningData.goal_distance, days: runDays.length, level: runningData.level });
                const kmNum = parseFloat(runningData.avg_weekly_km);
                const hasKm = Number.isFinite(kmNum) && kmNum > 0;
                const check = hasKm && runDays.length > 0
                  ? validateKmDaysMatrix(kmNum, runDays.length, { distance: runningData.goal_distance, level: runningData.level })
                  : null;
                const blocked = !!(check && !check.ok);
                const engineNo = !blocked && hasKm && planHint && planHint.supported === false;
                return (
                  <div id="pf-km-guide" className="-mt-2 mb-4 text-[12px] leading-snug" aria-live="polite" data-testid="pf-km-guide">
                    {/* the server's MASTER range for this goal / days / level when it answered, else the local text */}
                    <p className="text-text-muted" data-testid="pf-km-guide-text">{allowedRangeText(planHint, runDays.length) || guide.text}</p>
                    {blocked && (
                      <p className="text-quality mt-0.5" data-testid="pf-km-guide-status">הנפח שהוזן לא יעבור את בדיקת השמירה עם ההגדרות האלה.</p>
                    )}
                    {engineNo && (
                      <p className="text-quality mt-0.5 whitespace-pre-line" data-testid="pf-km-guide-status">{planHint.userMessage}</p>
                    )}
                  </div>
                );
              })()}

              <div className="flex items-baseline justify-between mb-2">
                <span className="text-[13px] text-text-muted" id="pf-days-label">ימי ריצה</span>
                <span className="text-[13px] font-semibold text-text-body" data-testid="pf-days-count">{runDays.length} ימים</span>
              </div>
              <DayChips ariaPrefix="יום ריצה" selected={isRunningDaySelected} onToggle={handleRunningDayToggle} />

              <div className="mt-4 mb-2 text-[13px] text-text-muted">ריצה ארוכה · מתוך ימי הריצה</div>
              <DayChips tone="long" ariaPrefix="ריצה ארוכה ביום"
                        selected={(k) => runningData.preferred_long_run_day === k}
                        disabled={(k) => !isRunningDaySelected(k)}
                        onToggle={(k) => setRunningData({
                          ...runningData,
                          preferred_long_run_day: runningData.preferred_long_run_day === k ? '' : k,
                        })} />
              <p className={`${HELPER} mt-2`}>ניתן לבחור רק מהימים שסומנו למעלה כימי ריצה. אם לא תבחר, נשים ביום האחרון שלך.</p>
            </section>

            {/* 3. מטרת הריצה — profile goal fields (not the Races screen). Name + date: optional pair. */}
            <section className={CARD} aria-labelledby="pf-goal" data-testid="pf-goal">
              <CardTitle><span id="pf-goal">מטרת הריצה</span></CardTitle>
              <label htmlFor="pf-race" className={LABEL}>שם המרוץ <span className="text-text-muted">(לא חובה)</span></label>
              <input id="pf-race" type="text" value={runningData.goal_race}
                     onChange={(e) => setRunningData({...runningData, goal_race: e.target.value})}
                     className={`${INPUT} border-border mb-3`} />
              {/* date on its own row: a native date input needs the full width to stay readable at 360 px */}
              <label htmlFor="pf-date" className={LABEL}>תאריך המרוץ <span className="text-text-muted">(לא חובה)</span></label>
              <input id="pf-date" type="date" value={runningData.goal_date}
                     onChange={(e) => setRunningData({...runningData, goal_date: e.target.value})}
                     className={`${INPUT} border-border mb-3`} />
              <div className="grid grid-cols-2 gap-3">
                <div className="min-w-0">
                  <label htmlFor="pf-distance" className={LABEL}>מרחק <span className="text-quality">*</span></label>
                  <select id="pf-distance" required value={runningData.goal_distance}
                          onChange={(e) => setRunningData({...runningData, goal_distance: e.target.value})}
                          className={`${INPUT} px-2 ${runningData.goal_distance ? 'border-border' : 'border-quality'}`}>
                    <option value="">בחר...</option>
                    <option value="5">5 ק"מ</option>
                    <option value="10">10 ק"מ</option>
                    <option value="21.1">חצי מרתון</option>
                    <option value="42.2">מרתון</option>
                  </select>
                </div>
                <div className="min-w-0">
                  <span className={LABEL} id="pf-time-label">זמן יעד <span className="text-quality">*</span></span>
                  <button type="button" aria-labelledby="pf-time-label" onClick={() => setGoalTimePickerOpen(true)}
                          className={`${INPUT} px-2 flex items-center justify-center ${runningData.goal_time ? 'border-border' : 'border-quality'}`}>
                    {runningData.goal_time
                      ? <bdi dir="ltr" className="font-bold text-accent tabular-nums" data-testid="pf-goal-time">{runningData.goal_time}</bdi>
                      : <span className="text-[14px] text-text-muted">בחר</span>}
                  </button>
                </div>
              </div>
              <p className={`${HELPER} mt-2`}>שם ותאריך המרוץ — לא חובה, אבל אם ממלאים אחד צריך גם את השני. מרחק וזמן יעד — חובה.</p>
            </section>

            {/* 4. בריאות ומצב פציעה */}
            <section className={CARD} aria-labelledby="pf-health" data-testid="pf-health">
              <CardTitle><span id="pf-health">בריאות ומצב פציעה</span></CardTitle>
              <Segmented label="מצב פציעה" value={runningData.injury_state} options={INJURY_OPTIONS} onChange={setInjuryState} />
              <p className="mt-2 text-[13px] leading-snug text-text-muted" data-testid="pf-injury-desc">{injury.desc} · התכנית מתאימה את עצמה למצב שנבחר</p>

              {runningData.injury_state !== 'ok' && (
                <div className="mt-3 space-y-3">
                  {sportsActive.length > 1 && (
                    <div>
                      <label htmlFor="pf-discipline" className={LABEL}>איזה ספורט מושפע? <span className="text-quality">* חובה</span></label>
                      <select id="pf-discipline" value={runningData.injury_discipline}
                              onChange={(e) => setRunningData({...runningData, injury_discipline: e.target.value})}
                              className={`${INPUT} ${!runningData.injury_discipline ? 'border-quality' : 'border-border'}`}>
                        <option value="">בחר ספורט</option>
                        <option value="run">ריצה בלבד</option>
                        <option value="road">רכיבה בלבד</option>
                      </select>
                      {!runningData.injury_discipline && (
                        <p className="text-quality text-[12px] mt-1">חובה לבחור ספורט — כדי שהתכנית תדע מה להתאים.</p>
                      )}
                    </div>
                  )}
                  <div>
                    <label htmlFor="pf-pain" className={LABEL}>מה בדיוק כואב? (תיאור חופשי)</label>
                    <input id="pf-pain" type="text" value={runningData.injury_description}
                           onChange={(e) => setRunningData({...runningData, injury_description: e.target.value})}
                           placeholder="למשל: שין ספלינטס שמאל, דלקת בגיד אכילס, רנר׳ס ני"
                           className={`${INPUT} border-border`} />
                  </div>
                </div>
              )}

              <div className="mt-3 border-t border-divider pt-1">
                <Collapsible title="היסטוריית פציעות ואזורים רגישים" summary={historySummary} testId="pf-history">
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="pf-prone" className={LABEL}>אזורים רגישים / היסטוריית פציעות</label>
                      <textarea id="pf-prone" rows={2} value={runningData.injury_prone_areas}
                                onChange={(e) => setRunningData({...runningData, injury_prone_areas: e.target.value})}
                                placeholder="למשל: ברך ימין, גיד אכילס..."
                                className="w-full rounded-control bg-surface-2 border border-border p-3 text-[16px] text-text resize-none focus:outline-none focus:ring-2 focus:ring-accent" />
                    </div>
                    <div>
                      <label htmlFor="pf-chronic" className={LABEL}>רגישויות כרוניות (היסטוריה, לא פעילה כעת)</label>
                      <input id="pf-chronic" type="text" value={runningData.chronic_sensitivities}
                             onChange={(e) => setRunningData({...runningData, chronic_sensitivities: e.target.value})}
                             placeholder="למשל: כאבי ברך ימין בעבר, נטייה לפציעת אכילס"
                             className={`${INPUT} border-border`} />
                      <p className={`${HELPER} mt-1`}>לא משנה נפח/עצימות — רק מוסיף טיפים מונעים לתכנית.</p>
                    </div>
                  </div>
                </Collapsible>
              </div>
            </section>

            {/* 5. נתונים פיזיולוגיים — collapsed by default */}
            <section className={`${CARD} py-1`} data-testid="pf-physio">
              <Collapsible title="נתונים פיזיולוגיים"
                           summary={runningData.max_hr ? `דופק מקס׳ ${runningData.max_hr}` : 'דופק מקסימלי לא הוזן'}
                           extra={zones ? <ZoneBar /> : null}>
                <div className="pb-3">
                  <label htmlFor="pf-maxhr" className={LABEL}>דופק מקסימלי (Max HR)</label>
                  <input id="pf-maxhr" type="number" inputMode="numeric" dir="ltr" value={runningData.max_hr}
                         onChange={(e) => setRunningData({...runningData, max_hr: e.target.value})} placeholder="185"
                         className={`${INPUT} border-border text-right`} />
                  {zones && (
                    <div className="mt-3" data-testid="pf-zones">
                      <p className="text-[13px] text-text-muted mb-2">אזורי דופק (לפי Max HR {runningData.max_hr})</p>
                      <div dir="ltr" className="grid grid-cols-5 gap-1.5">
                        {zones.map((z, i) => (
                          <div key={z.name} className="text-center min-w-0">
                            <div className="h-1 rounded-full mb-1.5" style={{ background: PROFILE_COLORS.zones[i] }} />
                            <div className="text-[12px] font-bold" style={{ color: PROFILE_COLORS.zones[i] }}>{z.name}</div>
                            <div className="text-[12px] text-text-body tabular-nums">{z.range}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Collapsible>
            </section>
          </>
        );
      })()}

      {/* 🚴 Cycling Profile — Phase 1: road-only */}
      {CYCLING_ENABLED && sportsActive.includes('cycling') && (
        <div className="p-4 rounded-xl bg-surface border border-border">
          <h3 className="text-text-body font-bold mb-4 flex items-center gap-2">פרופיל רכיבה · כביש</h3>

          {/* Level */}
          <div className="mb-4">
            <label className="block text-text-muted text-xs mb-1">רמת רוכב <span className="text-text-body">*</span></label>
            <select value={cyclingData.cycling_level} onChange={(e) => setCyclingData({...cyclingData, cycling_level: e.target.value})} className="w-full p-2 rounded-lg bg-bg border border-border text-text text-sm">
              <option value="">בחר רמה...</option>
              <option value="beginner">מתחיל (עד 5 ש׳/שבוע)</option>
              <option value="intermediate">בינוני (6–9 ש׳/שבוע)</option>
              <option value="advanced">מתקדם (9+ ש׳/שבוע)</option>
            </select>
          </div>

          {/* Preferred Days */}
          <div className="mb-4">
            <label className="block text-text-muted text-xs mb-2">ימי רכיבה מועדפים <span className="text-text-body">*</span></label>
            <div className="flex gap-2">
              {daysOfWeek.map((day) => (
                <button
                  key={day.key}
                  type="button"
                  onClick={() => handleCyclingDayToggle(day.key)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                    isCyclingDaySelected(day.key)
                      ? 'bg-accent text-on-accent'
                      : 'bg-bg border border-border text-text-muted hover:border-border'
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          {/* Weekly hours */}
          <div className="mb-4">
            <label className="block text-text-muted text-xs mb-1">שעות רכיבה שבועיות <span className="text-text-body">*</span></label>
            <input type="number" step="0.5" value={cyclingData.weekly_cycling_hours} onChange={(e) => setCyclingData({...cyclingData, weekly_cycling_hours: e.target.value})} placeholder="6" className="w-full p-2 rounded-lg bg-bg border border-border text-text text-sm" />
          </div>

          {/* Recent longest ride — optional */}
          <div className="mb-4">
            <label className="block text-text-muted text-xs mb-1">רכיבה ארוכה אחרונה (ק"מ) <span className="text-text-muted">(לא חובה)</span></label>
            <input type="number" step="1" value={cyclingData.recent_longest_ride_km} onChange={(e) => setCyclingData({...cyclingData, recent_longest_ride_km: e.target.value})} placeholder="80" className="w-full p-2 rounded-lg bg-bg border border-border text-text text-sm" />
          </div>

          {/* Max HR — optional */}
          <div className="mb-4">
            <label className="block text-text-muted text-xs mb-1">דופק מקסימלי Max HR <span className="text-text-muted">(לא חובה)</span></label>
            <input type="number" value={cyclingData.max_hr} onChange={(e) => setCyclingData({...cyclingData, max_hr: e.target.value})} placeholder="190" min="120" max="220" className="w-full p-2 rounded-lg bg-bg border border-border text-text text-sm" />
            <p className="text-text-muted text-[10px] mt-1">ריק — ישתמש בנוסחת 220 פחות גיל</p>
          </div>

          {/* Race goal — "no race" checkbox inverted (checked = no race) */}
          <div className="mb-4 border-t border-border pt-4">
            <label className="block text-text-muted text-xs mb-2">תחרות יעד</label>
            <div
              onClick={() => setCyclingData({...cyclingData, has_cycling_race: cyclingData.has_cycling_race === false ? null : false})}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all mb-3 ${
                cyclingData.has_cycling_race === false
                  ? 'bg-pressed border border-border'
                  : 'bg-bg border border-border hover:border-border'
              }`}
            >
              <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${
                cyclingData.has_cycling_race === false ? 'bg-accent' : 'bg-pressed border border-border'
              }`}>
                {cyclingData.has_cycling_race === false && <Check size={14} weight="bold" className="text-on-accent" aria-hidden="true" />}
              </div>
              <span className="text-text text-sm">מתאמן ללא תחרות — אין לי תחרות יעד</span>
            </div>

            {cyclingData.has_cycling_race !== false && (
              <div className="space-y-3">
                {/* Distance: 60/100/160 */}
                <div>
                  <label className="block text-text-muted text-xs mb-1">מרחק תחרות <span className="text-text-body">*</span></label>
                  <div className="flex gap-2">
                    {['60', '100', '160'].map(d => (
                      <button key={d} type="button"
                        onClick={() => setCyclingData({...cyclingData, cycling_goal_distance: d, has_cycling_race: true})}
                        className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all border ${
                          cyclingData.cycling_goal_distance === d
                            ? 'bg-pressed border-accent text-text'
                            : 'bg-bg border-border text-text-muted hover:border-border'
                        }`}
                      >{d} ק"מ</button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-text-muted text-xs mb-1">תאריך <span className="text-text-body">*</span></label>
                    <input type="date" value={cyclingData.cycling_goal_date} onChange={(e) => setCyclingData({...cyclingData, cycling_goal_date: e.target.value, has_cycling_race: true})} className="w-full p-2 rounded-lg bg-bg border border-border text-text text-sm" />
                  </div>
                  <div>
                    <label className="block text-text-muted text-xs mb-1">שם אירוע <span className="text-text-body">*</span></label>
                    <input type="text" value={cyclingData.cycling_goal_event} onChange={(e) => setCyclingData({...cyclingData, cycling_goal_event: e.target.value})} placeholder="גראן פונדו" className="w-full p-2 rounded-lg bg-bg border border-border text-text text-sm" />
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      )}

      {/* Save (Profile V2): the same saveAllProfiles flow — validation, modals, regenerate dialog — from a
          bar that appears only while the running-profile form has unsaved changes. Hidden while the
          regenerate dialog (also fixed at the bottom) is open. The spacer keeps the last card visible. */}
      {dirty && !regenerateToast && (
        <StickySaveBar saving={saving} onCancel={cancelEdits} onSave={() => saveAllProfiles()} />
      )}

      {/* Goal Time Picker Modal */}
      {goalTimePickerOpen && (
        <TimePicker
          label="בחר זמן יעד לריצה"
          value={runningData.goal_time}
          onChange={(v) => setRunningData({...runningData, goal_time: v})}
          onClose={() => setGoalTimePickerOpen(false)}
          distanceKm={runningData.goal_distance ? (
            runningData.goal_distance.includes('5') && !runningData.goal_distance.includes('50') ? 5 :
            runningData.goal_distance.includes('10') ? 10 :
            runningData.goal_distance.includes('חצי') || runningData.goal_distance.includes('21') ? 21.1 :
            runningData.goal_distance.includes('מרתון') || runningData.goal_distance.includes('42') ? 42.2 :
            null
          ) : null}
          showHours={true}
        />
      )}

      {guardrail && (
        <GoalGuardrailModal
          result={guardrail.result}
          onAdoptSuggestion={() => {
            // If the warning was triggered by a RACE in the calendar
            // (raceContext set), "downgrade to <distance>" must edit
            // THAT race — not the profile target. The profile field
            // wasn't the source of the warning; flipping it would
            // leave the marathon race intact and the next save would
            // re-fire the same warning. Dispatch a navigation event;
            // App.js routes the user to Races and Races.jsx opens
            // the edit modal pre-filled with the suggested distance.
            if (guardrail.raceContext && guardrail.raceContext.id) {
              // Leaving Profile for Races: the unsaved-changes guard asks first (the only confirmation on
              // this path — App's handler for this event does not ask again). Staying keeps the modal open.
              if (!confirmNavigation()) return;
              window.dispatchEvent(new CustomEvent('navigate-to-race-edit', {
                detail: {
                  raceId: guardrail.raceContext.id,
                  suggestedDistance: guardrail.result.suggestedDistance,
                },
              }));
              setGuardrail(null);
              return;
            }
            // Profile-triggered: update profile.goal_distance as before.
            const v = profileLabelToDistance(guardrail.result.suggestedDistance);
            if (v) {
              setRunningData((p) => ({ ...p, goal_distance: v, goal_time: '' }));
            }
            setGuardrail(null);
          }}
          onContinueAnyway={async () => {
            setGuardrail(null);
            await persistGuarded();
          }}
          onClose={() => setGuardrail(null)}
        />
      )}
      {saveDialogElement}
      <LoadBalanceModal
        open={loadBalanceModal.open}
        weeklyKm={loadBalanceModal.weeklyKm}
        trainingDays={loadBalanceModal.trainingDays}
        requiredDays={loadBalanceModal.requiredDays}
        onFix={() => setLoadBalanceModal({ open: false })}
      />
    </div>
  );
};

// Profile header (Graphite, 2026-10-27): name 28/800, the level muted, "Garmin מחובר" with a lime dot; then a stats
// row from the SAVED profile — level · weekly km · 10K PB. A value the profile does not carry is left out (never "—").
// Never shows unsaved edits.
function ProfileHeader({ profile, garmin, onBack }) {
  const p = profile || {};
  const level = getLevelLabel(p.level);
  const pr10k = typeof p.pr_10k === 'string' ? p.pr_10k.trim() : '';
  const garminConnected = !!(garmin && garmin.status === 'connected');
  const stats = [
    level && { key: 'level', l: 'רמה', v: (
      <span className="inline-flex items-center gap-1.5"><span className="text-[17px] font-bold">{level}</span><LevelMeter level={p.level} /></span>) },
    p.avg_weekly_km > 0 && { key: 'km', l: 'נפח שבועי',
      v: <><span className="num-display text-[28px]" dir="ltr">{p.avg_weekly_km}</span><span className="text-[13px] text-text-body ms-1">ק״מ</span></> },
    pr10k && { key: 'pb', l: 'שיא 10K', v: <span className="num-display text-[28px]" dir="ltr" data-testid="pf-stat-pb">{pr10k}</span> },
  ].filter(Boolean);
  return (
    <header className="space-y-3" data-testid="pf-header">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-[28px] font-extrabold text-text truncate leading-tight">{p.name || 'רץ'}</h1>
          {level && <p className="text-[14px] text-text-muted flex items-center gap-1.5">רץ · {level} <LevelMeter level={p.level} /></p>}
          {garmin && (
            <p className="mt-1 flex items-center gap-1.5 text-[13px] text-text-body"
               data-testid="pf-garmin-pill" aria-label={garminConnected ? 'Garmin מחובר' : 'Garmin לא מחובר'}>
              <span aria-hidden="true" className={`w-2 h-2 rounded-full ${garminConnected ? 'bg-accent' : 'bg-text-muted'}`} />
              <span><bdi dir="ltr">Garmin</bdi> {garminConnected ? 'מחובר' : 'לא מחובר'}</span>
            </p>
          )}
        </div>
        <button type="button" onClick={onBack} aria-label="חזרה"
                className="w-11 h-11 shrink-0 rounded-full border border-border text-text-body flex items-center justify-center">
          <span aria-hidden="true" className="text-[18px]">→</span>
        </button>
      </div>
      {stats.length > 0 && (
        <div className={`${CARD} py-3 flex`} data-testid="pf-stats">
          {stats.map((s, i) => (
            <div key={s.key} className={`flex-1 min-w-0 px-3 ${i ? 'border-s border-border' : 'ps-0'}`}>
              <div className="text-text truncate" style={{ lineHeight: 1.1 }}>{s.v}</div>
              <div className="text-[12px] text-text-muted mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      )}
    </header>
  );
}

function Profile({ onBack, userId, userEmail, onProfileSaved, onLogout, isNewUser = false, focusSection = null }) {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    fetchProfile();
    // Strava removal C2 (2026-10-21): a leftover Strava OAuth return (?strava_connected / ?strava_error, e.g. the
    // backend's strava_error=strava_removed redirect) is removed from the address silently — no message, no request.
    const params = new URLSearchParams(window.location.search);
    if (params.has('strava_connected') || params.has('strava_error') || params.has('strava_email')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      // Don't flip isLoading on refresh — the parent's `isLoading &&`
      // render path unmounts the entire SportsProfileSection while
      // it's true, which wipes the post-save regenerate-toast state.
      // Initial load already starts with isLoading=true (default state
      // of useState), so we only need to clear it after the first
      // successful fetch.
      const response = await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`);

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData(data);

        // If new user, start in edit mode
        if (isNewUser && !data.name) {
          setIsEditing(true);
        }
      } else if (response.status === 404) {
        // New user - no profile yet
        setProfile({});
        setFormData({});
        if (isNewUser) {
          setIsEditing(true);
        }
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      if (isNewUser) {
        setProfile({});
        setFormData({});
        setIsEditing(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Personal details (Profile V2): the date of birth is typed in three fields (DateOfBirthInput, 2026-10-20).
  // The partial day / month / year live HERE, so a re-render never loses them; a partial or impossible date
  // never reaches the server (its error shows on the attempted save). Empty is allowed: a legacy user keeps the
  // stored age. A failed save is shown instead of silently leaving the form open.
  const [personalError, setPersonalError] = useState('');
  const [dobParts, setDobParts] = useState({ day: '', month: '', year: '' });
  const [dobShowErrors, setDobShowErrors] = useState(false);
  const startEditing = () => {
    setDobParts(isoToParts(profile && profile.date_of_birth));
    setDobShowErrors(false);
    setPersonalError('');
    setIsEditing(true);
  };
  const handleSave = async () => {
    setPersonalError('');
    let _dobPayload = {};
    if (!isNewUser) {
      const _ev = evaluateDobParts(dobParts);
      if (_ev.status !== 'valid' && _ev.status !== 'empty') { setDobShowErrors(true); return; }
      _dobPayload = { date_of_birth: _ev.status === 'valid' ? _ev.iso : null };
    }
    try {
      setIsSaving(true);
      // ✅ Include user_id and email in body
      const payload = {
        ...formData,
        ..._dobPayload,
        user_id: userId,
        email: userEmail  // 🔧 FIX: שמירת email מ-Firebase
      };
      const response = await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setIsEditing(false);
        
        // ✅ Call onProfileSaved callback!
        if (onProfileSaved) {
          console.log('✅ Profile saved, calling onProfileSaved');
          onProfileSaved();
        }
      } else {
        let _msg = 'השמירה נכשלה. נסה שוב.';
        try {
          const _b = await response.json();
          const _d = _b && _b.detail;
          if (Array.isArray(_d) && _d.length) _msg = String(_d[0].msg || _msg).replace(/^Value error,\s*/i, '');
          else if (typeof _d === 'string') _msg = _d;
        } catch { /* keep the generic message */ }
        setPersonalError(_msg);
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      setPersonalError('השמירה נכשלה — בדוק חיבור לאינטרנט ונסה שוב.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Profile V2 (2026-10-19): Garmin status for the header pill comes from the EXISTING Garmin row's own
  // /garmin/status poll (no extra request); null = Garmin not shown for this user -> no pill.
  const [garminInfo, setGarminInfo] = useState(null);
  // While the save bar is up, the page gets bottom room so the last card is never hidden under it.
  const [profileDirty, setProfileDirty] = useState(false);
  // Leaving with unsaved running-profile changes asks first (navigationGuard, set by SportsProfileSection).
  const handleBack = () => { if (confirmNavigation() && onBack) onBack(); };

  if (!isNewUser) {
    return (
      <div className="min-h-screen bg-bg" dir="rtl">
        <main className={`max-w-2xl mx-auto px-4 pt-4 space-y-4 ${profileDirty ? 'pb-28' : 'pb-8'}`}>
          <ProfileHeader profile={profile} garmin={garminInfo} onBack={handleBack} />

          {isLoading && (
            <div className={CARD}><p className="text-text-muted text-center animate-pulse">טוען פרופיל...</p></div>
          )}

          {/* Profile could not be loaded (not a 404): the same way in as before. */}
          {!isLoading && !profile && (
            <div className={`${CARD} text-center`}>
              <p className="text-text-body mb-4">בוא נכיר אותך כדי שנוכל לבנות תכנית אימונים מותאמת אישית</p>
              <button type="button" onClick={() => { setProfile({}); setFormData({}); setIsEditing(true); }}
                      className="h-12 px-6 rounded-control btn-primary text-[16px]">
                התחל למלא פרופיל
              </button>
            </div>
          )}

          {!isLoading && profile && (
            <>
              {/* 2-5: training / goal / health / physiology (+ cycling when enabled) and the save bar */}
              <SportsProfileSection userId={userId} onUpdate={fetchProfile} onBack={onBack} onDirtyChange={setProfileDirty}
                                    focusSection={focusSection} />

              {/* 6. Connected services - the existing section and rows, unchanged logic */}
              <TrainingConnectionsSection
                onGarminStatus={setGarminInfo}
              />

              {/* 7. Notifications - server-side preferences (auto-save) + this device's status */}
              <section aria-labelledby="pf-notif">
                <SectionLabel id="pf-notif">התראות</SectionLabel>
                <div className={CARD}>
                  <NotificationSettings userId={userId} apiBase={API_BASE_URL} />
                </div>
              </section>

              {/* 8. Account - personal details (own edit / save, unchanged), sign-out, account deletion */}
              <section aria-labelledby="pf-account" className="space-y-3">
                <SectionLabel id="pf-account">חשבון</SectionLabel>
                <div className={`${CARD} py-1`}>
                  <Collapsible title="פרטים אישיים" summary="שם, תאריך לידה, אימייל" testId="pf-personal">
                    <div className="pb-3 space-y-3">
                      <div className="flex justify-end">
                        <button type="button" onClick={() => (isEditing ? setIsEditing(false) : startEditing())}
                                className="h-11 px-4 rounded-control border border-border text-text font-semibold text-[14px]">
                          {isEditing ? 'ביטול' : 'עריכה'}
                        </button>
                      </div>
                      <div>
                        <label htmlFor="pf-name" className={LABEL}>שם *</label>
                        {isEditing ? (
                          <input id="pf-name" type="text" value={formData.name || ''} placeholder="השם שלך"
                                 onChange={(e) => handleChange('name', e.target.value)}
                                 className={`${INPUT} border-border`} />
                        ) : <p className="text-[16px] text-text">{profile.name || '-'}</p>}
                      </div>
                      <div>
                        {/* Date of birth: the source of truth for age when present. Editing: the three-field input, whose
                            status line is the ONE age indication while editing. Viewing: the age the backend returned
                            (authoritative). A legacy profile without a date keeps its stored age, never an invented date. */}
                        {isEditing ? (
                          <DateOfBirthInput idPrefix="pf-dob" parts={dobParts} onPartsChange={setDobParts}
                                            showErrors={dobShowErrors} />
                        ) : (
                          <>
                            <span className={LABEL}>תאריך לידה</span>
                            <p className="text-[16px] text-text" data-testid="pf-dob-value">
                              {profile.date_of_birth
                                ? <bdi dir="ltr">{String(profile.date_of_birth).slice(0, 10).split('-').reverse().join('/')}</bdi>
                                : <span className="text-text-muted">לא הוזן</span>}
                            </p>
                          </>
                        )}
                      </div>
                      {!isEditing && (
                        <div>
                          <span className={LABEL}>גיל</span>
                          <p className="text-[16px] text-text" data-testid="pf-age-value">
                            {profile.age ?? '-'}
                            {!profile.date_of_birth && profile.age
                              ? <span className="text-[12px] text-text-muted"> · הוסף תאריך לידה כדי שהגיל יתעדכן אוטומטית</span> : null}
                          </p>
                        </div>
                      )}
                      {userEmail && (
                        <div>
                          <span className={LABEL}>אימייל</span>
                          <p className="h-[46px] flex items-center justify-end rounded-control bg-surface-2 px-3 text-[16px] text-text-body">
                            <bdi dir="ltr">{userEmail}</bdi>
                          </p>
                        </div>
                      )}
                      {personalError && (
                        <p role="alert" className="text-quality text-[13px]" data-testid="pf-personal-error">{personalError}</p>
                      )}
                      {isEditing && (
                        <button type="button" onClick={handleSave} disabled={isSaving}
                                className="w-full h-12 rounded-control btn-primary text-[16px]">
                          {isSaving ? 'שומר...' : 'שמור פרטים אישיים'}
                        </button>
                      )}
                    </div>
                  </Collapsible>
                </div>
                {/* Google Sign-In (Option B): set a password through the account's own email (flag OFF -> not rendered) */}
                {GOOGLE_SIGNIN_ENABLED && <PasswordSetupSection />}
                {onLogout && (
                  <button type="button" onClick={onLogout}
                          className="w-full h-12 rounded-control border border-border text-quality font-bold text-[15px]">
                    התנתקות מהחשבון
                  </button>
                )}
                {onLogout && <DeleteAccountSection userId={userId} API_BASE_URL={API_BASE_URL} onDeleted={onLogout} />}
              </section>
            </>
          )}
        </main>
      </div>
    );
  }

  // New-user path (isNewUser - App.jsx onboarding): unchanged from before Profile V2.
  return (
    <div className="min-h-screen bg-bg">
    <div className="max-w-2xl mx-auto" dir="rtl">


      {/* Header */}
      <header className="sticky top-0 z-20 bg-bg border-b border-border">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-pressed text-text-body flex items-center justify-center">
                <Gear size={20} weight="bold" aria-hidden="true" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-text">
                  {isNewUser ? 'יצירת פרופיל' : 'הגדרות ופרופיל'}
                </h1>
                <p className="text-xs text-text-muted">
                  {isNewUser ? 'ספר לנו על עצמך' : 'עדכן פרטים ומטרות'}
                </p>
              </div>
            </div>
            
            {!isNewUser && (
              <button
                onClick={onBack}
                aria-label="למסך הבית"
                className="w-11 h-11 rounded-full border border-border flex items-center justify-center text-text-body"
              >
                <House size={20} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-6">
        <div className="max-w-2xl mx-auto">
          
          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-surface text-text-muted flex items-center justify-center mb-4 animate-pulse">
                  <User size={28} weight="bold" aria-hidden="true" />
                </div>
                <p className="text-text-muted">טוען פרופיל...</p>
              </div>
            </div>
          )}

          {/* New User - No Profile Yet */}
          {!isLoading && !profile && (
            <div className="rounded-2xl bg-surface border border-border p-6 mb-6 text-center">
              <div className="w-20 h-20 mx-auto rounded-2xl bg-pressed text-accent flex items-center justify-center mb-4">
                <HandWaving size={36} weight="bold" aria-hidden="true" />
              </div>
              <h2 className="text-xl font-bold text-text mb-2">ברוך הבא!</h2>
              <p className="text-text-muted mb-6">בוא נכיר אותך כדי שנוכל לבנות תכנית אימונים מותאמת אישית</p>
              <button
                onClick={() => {
                  setProfile({});
                  setFormData({});
                  setIsEditing(true);
                }}
                className="px-6 py-3 rounded-xl btn-primary transition-all"
              >
                התחל למלא פרופיל
              </button>
            </div>
          )}

          {!isLoading && profile && (
            <>
              {/* New User Welcome Banner */}
              {isNewUser && (
                <div className="rounded-2xl bg-surface border border-border p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <HandWaving size={28} weight="bold" className="text-accent shrink-0" aria-hidden="true" />
                    <div>
                      <h2 className="text-lg font-bold text-text">ברוך הבא!</h2>
                      <p className="text-text-muted text-sm">מלא את הפרטים ולחץ "שמור" כדי להמשיך</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Profile Header */}
              {!isNewUser && (
                <div className="rounded-2xl bg-surface border border-border p-5 mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-pressed text-text-body flex items-center justify-center">
                      <User size={28} weight="bold" aria-hidden="true" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-text">{profile.name || 'רץ'}</h2>
                      <p className="text-text-body">{getLevelLabel(profile.level) || 'מתחיל'} • גיל {profile.age || '?'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* 1️⃣ Details Card - Personal Info Only - AT THE TOP */}
              <div className="rounded-2xl bg-surface border border-border overflow-hidden mb-6">
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <h3 className="text-text font-semibold">פרטים אישיים</h3>
                  {!isNewUser && (
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className="text-sm px-3 py-1 rounded-lg bg-pressed text-text-body hover:bg-pressed transition"
                    >
                      {isEditing ? 'ביטול' : 'עריכה'}
                    </button>
                  )}
                </div>
                
                <div className="p-4 space-y-4">
                  {/* Name */}
                  <div>
                    <label className="text-text-muted text-sm block mb-1">שם *</label>
                    {isEditing || isNewUser ? (
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className="w-full bg-bg border border-border rounded-xl px-4 py-2 text-text focus:border-border focus:outline-none"
                        placeholder="השם שלך"
                      />
                    ) : (
                      <p className="text-text">{profile.name || '-'}</p>
                    )}
                  </div>

                  {/* Age */}
                  <div>
                    <label className="text-text-muted text-sm block mb-1">גיל *</label>
                    {isEditing || isNewUser ? (
                      <input
                        type="number"
                        value={formData.age || ''}
                        onChange={(e) => handleChange('age', parseInt(e.target.value))}
                        className="w-full bg-bg border border-border rounded-xl px-4 py-2 text-text focus:border-border focus:outline-none"
                        placeholder="הגיל שלך"
                      />
                    ) : (
                      <p className="text-text">{profile.age || '-'}</p>
                    )}
                  </div>

                  {/* Email - Display only */}
                  {userEmail && (
                    <div>
                      <label className="text-text-muted text-sm block mb-1">אימייל</label>
                      <p className="text-text">{userEmail}</p>
                    </div>
                  )}

                  {/* Required Fields Note for New Users */}
                  {isNewUser && (
                    <div className="bg-pressed border border-border rounded-xl p-3 mt-4">
                      <p className="text-text-body text-sm">
                        * שדות חובה - יש למלא כדי להמשיך
                      </p>
                    </div>
                  )}

                  {/* Save Button */}
                  {(isEditing || isNewUser) && (
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="w-full mt-4 py-3 rounded-xl btn-primary transition-all"
                    >
                      {isSaving ? 'שומר...' : isNewUser ? 'שמור והמשך' : 'שמור שינויים'}
                    </button>
                  )}
                </div>
              </div>

              {/* 2️⃣ Training connections (Garmin) — one section, compact rows (2026-10-11; Strava removed C2) */}
              {!isNewUser && (
                <TrainingConnectionsSection
                />
              )}

              {/* Notifications — server-side preferences (same source as onboarding Step 9) + this device's status */}
              {!isNewUser && (
                <div className="rounded-2xl p-4 mb-6" style={{ background: 'var(--g-card)', border: '1px solid var(--g-line)' }}>
                  <NotificationSettings userId={userId} apiBase={API_BASE_URL} />
                </div>
              )}

              {/* 3️⃣ Sports Selection & Profiles - Only for existing users */}
              {!isNewUser && (
                <div className="mb-6">
                  <SportsProfileSection userId={userId} onUpdate={fetchProfile} onBack={onBack} focusSection={focusSection} />
                </div>
              )}

              {/* Sign-out — moved here from the home header (A2 redesign §1.1).
                  Quiet, at the very bottom: an account action, not a daily one. */}
              {!isNewUser && onLogout && (
                <div className="mb-8">
                  <button
                    onClick={onLogout}
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: 12,
                      background: 'transparent', border: '1px solid rgba(251,146,60,.35)',
                      color: '#fb923c', fontFamily: "'Heebo',sans-serif",
                      fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    }}
                  >
                    התנתקות מהחשבון
                  </button>
                </div>
              )}

              {/* Delete account — permanent, multi-step confirmation (DeleteAccountSection). */}
              {!isNewUser && onLogout && (
                <div className="mb-8">
                  <DeleteAccountSection userId={userId} API_BASE_URL={API_BASE_URL} onDeleted={onLogout} />
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
    </div>
  );
}

export default Profile;
